import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { PlusIcon, SearchIcon, CalendarIcon, ClockIcon, ListIcon, XIcon, CheckIcon, EyeIcon, EditIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon, LockIcon, StethoscopeIcon, FileTextIcon, RefreshCwIcon, GiftIcon, UserIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { FormSection } from '../ui/FormSection';
import { SortableHeader } from '../ui/SortableHeader';
import { Pagination } from '../ui/Pagination';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { useSortableData } from '../../hooks/useSortableData';
import { usePagination } from '../../hooks/usePagination';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Appointment, GiftCertificate } from '../../types';
import { getToday, getTomorrow, formatTime, formatCurrency, getDoctorPrefix, getDaysInMonth, getFirstDayOfMonth, getMonthName, getDayOfWeekKey, getDayOfWeekName } from '../../utils/helpers';
import { validateAppointmentForm } from '../../utils/validation';
import { sendDesktopNotification } from '../../utils/desktopNotifications';
import { emailNotificationService } from '../../utils/emailNotifications';
import { supabase } from '../../utils/supabase';
import { exportAppointmentsToExcel } from '../../utils/excelExport';
import { getAppointmentOfferInfo, getServiceOfferInfo as getSharedServiceOfferInfo } from '../../utils/appointmentPricing';
import { exportAppointmentsToPdf } from '../../utils/pdfExport';
import { HelpTooltip } from './HelpTooltip';
interface AppointmentsProps {
  initialDateFilter?: string;
  autoOpenNew?: boolean;
}
interface LoyaltyCardForRedeem {
  id: string;
  code: string;
  service_ids: string[];
}
export function Appointments({
  initialDateFilter,
  autoOpenNew
}: AppointmentsProps) {
  const {
    appointments,
    doctors,
    patients,
    services,
    specialties,
    getPatient,
    getDoctor,
    getService,
    getActiveOfferForService,
    getActivePackageDiscount,
    getActiveGiftCertificates,
    redeemGiftCertificate,
    getSpecialty,
    getDoctorsBySpecialty,
    checkDoctorAvailability,
    getAppointmentsByDate,
    addAppointment,
    updateAppointment,
    cancelAppointment,
    rescheduleAppointment,
    completeAppointment,
    addAuditLog
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    addNotification
  } = useNotifications();
  const {
    confirm
  } = useConfirm();
  const {
    isAdmin,
    canExport,
    getPermissionLevel
  } = useRolePermissions();
  const canExportExcel = canExport('appointments');
  const canExportPdf = canExport('appointments');
  const appointmentPermissionLevel = getPermissionLevel('appointments');
  const isOwnOnly = appointmentPermissionLevel === 'own';
  const isReadOnlyAppointments = appointmentPermissionLevel === 'read';
  const canCreateAppointments = appointmentPermissionLevel === 'full';
  const linkedDoctor = isOwnOnly && user ? doctors.find(d => !!d.email && !!user.email && d.email.toLowerCase().trim() === user.email.toLowerCase().trim()) : null;
  const hasOwnButNoDoctor = isOwnOnly && !linkedDoctor;
  const [activeView, setActiveView] = useState<'list' | 'calendar'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDoctorId, setFilterDoctorId] = useState('');
  const [filterDate, setFilterDate] = useState(initialDateFilter || '');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const pickerBtnRef = useRef<HTMLButtonElement>(null);
  const [pickerNavDate, setPickerNavDate] = useState(new Date());
  // El calendario del selector de fecha se renderiza en un portal (document.body),
  // fuera del árbol de pickerRef. Sin esta referencia adicional, cualquier clic
  // DENTRO del calendario (por ejemplo, elegir un día) se detectaba como "clic
  // afuera" y cerraba el popup antes de registrar la selección — por eso elegir
  // una fecha parecía no funcionar.
  const pickerPopupRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isDatePickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedInsideTrigger = pickerRef.current && pickerRef.current.contains(target);
      const clickedInsidePopup = pickerPopupRef.current && pickerPopupRef.current.contains(target);
      if (!clickedInsideTrigger && !clickedInsidePopup) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDatePickerOpen]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [isActionSelectOpen, setIsActionSelectOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [formData, setFormData] = useState({
    patientId: '',
    doctorId: '',
    serviceIds: [] as string[],
    date: getToday(),
    time: '09:00',
    notes: ''
  });
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [overrideRestrictions, setOverrideRestrictions] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    time: ''
  });
  const [completeData, setCompleteData] = useState({
    nextAppointmentDate: '',
    nextAppointmentTime: '',
    nextAppointmentNotes: '',
    nextServiceIds: [] as string[],
    invoiceNumber: ''
  });
  // Certificado de regalo a canjear al completar esta cita (id) — debe coincidir
  // con la persona y el servicio de la cita.
  const [certToRedeem, setCertToRedeem] = useState<string>('');
  const [loyaltyCardToRedeem, setLoyaltyCardToRedeem] = useState<string>('');
  const [loyaltyCards, setLoyaltyCards] = useState<LoyaltyCardForRedeem[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [doctorStatuses, setDoctorStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [specialtyStatuses, setSpecialtyStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    const loadStatuses = () => {
      try {
        const ds = localStorage.getItem('savidna-doctors-status');
        if (ds) setDoctorStatuses(JSON.parse(ds));
        const ss = localStorage.getItem('savidna-specialties-status');
        if (ss) setSpecialtyStatuses(JSON.parse(ss));
        const svs = localStorage.getItem('savidna-services-status');
        if (svs) setServiceStatuses(JSON.parse(svs));
        const ps = localStorage.getItem('savidna-patients-status');
        if (ps) setPatientStatuses(JSON.parse(ps));
      } catch {}
    };
    loadStatuses();
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-doctors-status' && e.newValue) {
        try {
          setDoctorStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-specialties-status' && e.newValue) {
        try {
          setSpecialtyStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-services-status' && e.newValue) {
        try {
          setServiceStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-patients-status' && e.newValue) {
        try {
          setPatientStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isDoctorActive = (id: string) => !doctorStatuses[id];
  const isSpecialtyActive = (id: string) => !specialtyStatuses[id];
  const isServiceActive = (id: string) => !serviceStatuses[id];
  const isPatientActive = (id: string) => !patientStatuses[id];
  const today = getToday();
  // Convierte una hora en formato "10:30 AM" / "8:00 AM" a "22:30" / "08:00"
  // (24 horas) para poder ordenarla correctamente como texto.
  const convertTo24Hour = (time: string): string => {
    if (!time) return '00:00';
    const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (!match) return time; // Ya viene en 24h u otro formato: se usa tal cual
    let [, hourStr, minuteStr, period] = match;
    let hour = parseInt(hourStr, 10);
    if (period) {
      const upperPeriod = period.toUpperCase();
      if (upperPeriod === 'PM' && hour !== 12) hour += 12;
      if (upperPeriod === 'AM' && hour === 12) hour = 0;
    }
    return `${String(hour).padStart(2, '0')}:${minuteStr}`;
  };
  const filteredAppointments = appointments.filter(apt => {
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const matchesSearch = !searchTerm || patient?.name.toLowerCase().includes(searchTerm.toLowerCase()) || doctor?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || apt.status === filterStatus;
    const matchesDoctor = !filterDoctorId || apt.doctorId === filterDoctorId;
    const matchesDate = !filterDate || apt.date === filterDate;
    const matchesOwnership = isOwnOnly && linkedDoctor ? apt.doctorId === linkedDoctor.id : true;
    return matchesSearch && matchesStatus && matchesDoctor && matchesDate && matchesOwnership;
  }).map(apt => ({
    ...apt,
    // Campo auxiliar para ordenar por fecha Y hora juntas (no solo fecha).
    dateTimeSort: `${apt.date} ${convertTo24Hour(apt.time)}`
  }));
  const {
    sortedItems: sortedAppointments,
    requestSort: aptRequestSort,
    isSorted: aptIsSorted
  } = useSortableData(filteredAppointments);
  const {
    paginatedItems: paginatedAppointments,
    currentPage: aptPage,
    totalPages: aptTotalPages,
    totalItems: aptTotalItems,
    startIndex: aptStartIndex,
    endIndex: aptEndIndex,
    goToPage: aptGoToPage,
    nextPage: aptNextPage,
    prevPage: aptPrevPage
  } = usePagination(sortedAppointments);
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'programada',
    label: 'Programada'
  }, {
    value: 'completada',
    label: 'Completada'
  }, {
    value: 'cancelada',
    label: 'Cancelada'
  }, {
    value: 'reprogramada',
    label: 'Reprogramada'
  }];

  // Filtro por doctor en la lista de citas — útil para ver de un vistazo todas
  // las citas de un doctor específico (ej: "¿a quién ha atendido el Dr. Pérez?").
  // No se muestra si el usuario logueado ya está restringido a sus propias citas.
  const doctorFilterOptions = [{
    value: '',
    label: 'Todos los doctores'
  }, ...doctors.filter(d => isDoctorActive(d.id)).map(d => ({
    value: d.id,
    label: d.name
  }))];
  const patientOptions = patients.filter(p => isPatientActive(p.id)).map(p => ({
    value: p.id,
    label: p.name
  }));
  const specialtyOptions = [{
    value: '',
    label: 'Todas las especialidades'
  }, ...specialties.filter(s => {
    if (!isSpecialtyActive(s.id)) return false;
    if (formData.doctorId) {
      const selectedDoctor = doctors.find(d => d.id === formData.doctorId);
      return selectedDoctor ? s.id === selectedDoctor.specialtyId : true;
    }
    return true;
  }).map(s => ({
    value: s.id,
    label: s.name
  }))];
  const doctorOptions = selectedSpecialty ? getDoctorsBySpecialty(selectedSpecialty).filter(d => isDoctorActive(d.id)).map(d => ({
    value: d.id,
    label: `${getDoctorPrefix(d.gender, getSpecialty(d.specialtyId)?.name)} ${d.name}`
  })) : doctors.filter(d => isDoctorActive(d.id)).map(d => ({
    value: d.id,
    label: `${getDoctorPrefix(d.gender, getSpecialty(d.specialtyId)?.name)} ${d.name}`
  }));
  const availableServices = services.filter(s => isServiceActive(s.id));
  const filteredFormServices = availableServices.filter(s => serviceSearchTerm ? s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) : true);
  const toggleService = (serviceId: string) => {
    setFormData(prev => {
      const currentIds = prev.serviceIds || [];
      const newIds = currentIds.includes(serviceId) ? currentIds.filter(id => id !== serviceId) : [...currentIds, serviceId];
      return {
        ...prev,
        serviceIds: newIds
      };
    });
  };
  const selectedServicesList = availableServices.filter(s => formData.serviceIds.includes(s.id));
  const totalDuration = selectedServicesList.reduce((sum, s) => sum + s.duration, 0);
  const totalPrice = selectedServicesList.reduce((sum, s) => sum + s.price, 0);
  // Igual que getServiceOfferInfo, pero para una CITA YA EXISTENTE.
  // - Si el descuento guardado viene de un PAQUETE de sesiones, se respeta el
  //   valor guardado tal cual (registro histórico, para no alterar reportes/
  //   comisiones si el paquete ya se completó o cambió).
  // - Si el descuento guardado viene de una OFERTA general, se revalida EN VIVO
  //   contra las ofertas activas actuales: si la oferta cambió de rango/porcentaje
  //   o ya no está activa para la fecha de la cita, el descuento se actualiza o
  //   desaparece automáticamente, sin necesidad de tocar la cita manualmente.
  // Delegan en utils/appointmentPricing para que Dashboard, Comisiones y los
  // exports de ingresos calculen exactamente lo mismo. Antes esta lógica solo
  // existía aquí y los otros módulos mostraban el precio de lista.
  const pricingDeps = {
    services,
    getPatient,
    getActivePackageDiscount,
    getActiveOfferForService
  };
  const getStoredOfferInfo = (appointment: Appointment, serviceId: string) => getAppointmentOfferInfo(pricingDeps, appointment, serviceId);
  const getServiceOfferInfo = (serviceId: string, date = formData.date, patientId = formData.patientId) => getSharedServiceOfferInfo(pricingDeps, serviceId, date, patientId);
  const totalDiscount = selectedServicesList.reduce((sum, s) => sum + getServiceOfferInfo(s.id).discountAmount, 0);
  const totalWithOffers = Math.max(0, totalPrice - totalDiscount);
  const buildServiceDiscounts = (serviceIds: string[], date: string, patientId = formData.patientId) => {
    const map: Record<string, number> = {};
    serviceIds.forEach(sId => {
      // Igual que en getServiceOfferInfo: el descuento del paquete tiene
      // prioridad sobre cualquier oferta general.
      const packageDiscountPercent = patientId ? getActivePackageDiscount(patientId, sId) : undefined;
      if (packageDiscountPercent !== undefined) {
        map[sId] = packageDiscountPercent;
        return;
      }
      const offer = getActiveOfferForService(sId, date);
      if (offer) map[sId] = offer.discountPercent;
    });
    return map;
  };
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);
  const getDateString = (day: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => {
      const matchesDate = a.date === dateStr && a.status !== 'cancelada';
      const matchesOwnership = isOwnOnly && linkedDoctor ? a.doctorId === linkedDoctor.id : true;
      const matchesDoctor = !filterDoctorId || a.doctorId === filterDoctorId;
      return matchesDate && matchesOwnership && matchesDoctor;
    }).length;
  };
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const selectedDateAppointments = getAppointmentsByDate(selectedDate);
  const openNewForm = () => {
    setSelectedAppointment(null);
    setFormData({
      patientId: '',
      doctorId: isOwnOnly && linkedDoctor ? linkedDoctor.id : '',
      serviceIds: [],
      date: getToday(),
      time: '09:00',
      notes: ''
    });
    setSelectedSpecialty('');
    setFormErrors({});
    setOverrideRestrictions(false);
    setIsFormOpen(true);
  };
  useEffect(() => {
    if (autoOpenNew) {
      const timer = setTimeout(() => openNewForm(), 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);
  const openEditModal = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    const serviceIds = appointment.serviceIds && appointment.serviceIds.length > 0 ? appointment.serviceIds : [appointment.serviceId];
    setFormData({
      patientId: appointment.patientId,
      doctorId: appointment.doctorId,
      serviceIds,
      date: appointment.date,
      time: appointment.time,
      notes: appointment.notes || ''
    });
    const doctor = getDoctor(appointment.doctorId);
    if (doctor) setSelectedSpecialty(doctor.specialtyId);
    setFormErrors({});
    setOverrideRestrictions(false);
    setIsEditOpen(true);
  };
  const openCancelModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setCancelReason('');
    setIsCancelOpen(true);
  };
  const openRescheduleModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({
      date: appointment.date,
      time: appointment.time
    });
    setOverrideRestrictions(false);
    setIsRescheduleOpen(true);
  };
  const openCompleteModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    const currentServiceIds = appointment.serviceIds && appointment.serviceIds.length > 0 ? appointment.serviceIds : [appointment.serviceId].filter(Boolean);
    setCompleteData({
      nextAppointmentDate: '',
      nextAppointmentTime: appointment.time || '09:00',
      nextAppointmentNotes: '',
      nextServiceIds: [...currentServiceIds],
      // Faltaba: al abrir el modal quedaba el número tecleado en la cita
      // anterior, listo para guardarse en la nueva por descuido.
      invoiceNumber: ''
    });
    setCertToRedeem('');
    setLoyaltyCardToRedeem('');
    // Las tarjetas se consultan por paciente y se filtran nuevamente por
    // servicio al mostrarlas. Así el canje nunca se ofrece a otra persona.
    supabase.from('loyalty_cards').select('id, code, service_ids').eq('patient_id', appointment.patientId).eq('status', 'activa').then(({
      data
    }) => {
      setLoyaltyCards((data || []) as LoyaltyCardForRedeem[]);
    }).catch(() => setLoyaltyCards([]));
    setServiceSearchTerm('');
    setIsCompleteOpen(true);
  };
  const openActionSelectModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsActionSelectOpen(true);
  };
  const openDetailModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsDetailOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateAppointmentForm(formData, {
      allowPastDate: true
    });
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const selectedDoctor = getDoctor(formData.doctorId);
    if (!overrideRestrictions && selectedDoctor && selectedDoctor.schedule) {
      const dayKey = getDayOfWeekKey(formData.date) as keyof typeof selectedDoctor.schedule;
      const daySchedule = selectedDoctor.schedule[dayKey];
      const dayName = getDayOfWeekName(formData.date);
      const prefix = getDoctorPrefix(selectedDoctor.gender, getSpecialty(selectedDoctor.specialtyId)?.name);
      if (!daySchedule) {
        setFormErrors({
          date: `${prefix} ${selectedDoctor.name} no trabaja los ${dayName}`
        });
        return;
      }
      const requestedMinutes = parseInt(formData.time.split(':')[0]) * 60 + parseInt(formData.time.split(':')[1]);
      const startMinutes = parseInt(daySchedule.start.split(':')[0]) * 60 + parseInt(daySchedule.start.split(':')[1]);
      const endMinutes = parseInt(daySchedule.end.split(':')[0]) * 60 + parseInt(daySchedule.end.split(':')[1]);
      if (requestedMinutes < startMinutes || requestedMinutes >= endMinutes) {
        setFormErrors({
          time: `Fuera del horario. ${dayName}: ${formatTime(daySchedule.start)} - ${formatTime(daySchedule.end)}`
        });
        return;
      }
    }
    if (!overrideRestrictions && !checkDoctorAvailability(formData.doctorId, formData.date, formData.time)) {
      setFormErrors({
        time: 'El doctor ya tiene una cita en este horario o dentro del margen de 30 minutos entre citas'
      });
      return;
    }
    const patient = getPatient(formData.patientId);
    const doctor = getDoctor(formData.doctorId);
    const selectedServices = services.filter(s => formData.serviceIds.includes(s.id));
    const serviceNames = selectedServices.map(s => s.name).join(', ');
    const duration = selectedServices.reduce((sum, s) => sum + s.duration, 0) || 30;
    confirm({
      title: 'Guardar Cita',
      message: '¿Estás seguro de que deseas guardar esta nueva cita?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          await addAppointment({
            ...formData,
            serviceId: formData.serviceIds[0],
            serviceDiscounts: buildServiceDiscounts(formData.serviceIds, formData.date),
            duration,
            status: 'programada'
          }, overrideRestrictions);
        } catch (err: any) {
          logger.error('Error creando cita:', err);
          showToast({
            type: 'error',
            title: 'Error al crear la cita',
            message: err?.message || 'No se pudo guardar la cita. Intenta de nuevo.'
          });
          addNotification({
            type: 'appointment_cancelled',
            title: 'Error al crear la cita',
            message: err?.message || 'No se pudo guardar la cita. Intenta de nuevo.',
            relatedId: formData.patientId
          });
          return;
        }
        addNotification({
          type: 'appointment_created',
          title: 'Nueva Cita Creada',
          message: `Cita para ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} el ${formData.date} a las ${formatTime(formData.time)} — ${serviceNames}`,
          relatedId: formData.patientId
        });
        sendDesktopNotification({
          title: '🗓️ Nueva Cita Creada',
          body: `${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'}\n${formData.date} a las ${formatTime(formData.time)}\n${serviceNames}`,
          tag: `appointment-created-${formData.patientId}-${formData.date}-${formData.time}`
        });
        if (patient && doctor && selectedServices.length > 0) {
          emailNotificationService.sendAppointmentCreated({
            patientName: patient.name,
            patientEmail: patient.email,
            doctorName: doctor.name,
            serviceName: serviceNames,
            date: formData.date,
            time: formData.time,
            duration,
            notes: formData.notes
          }).then(result => {
            if (result.success) {
              addNotification({
                type: 'appointment_created',
                title: '📧 Correo Enviado',
                message: `Notificación enviada a ${patient.email}`,
                relatedId: formData.patientId
              });
            } else if (emailNotificationService.isEnabled()) {
              addNotification({
                type: 'appointment_cancelled',
                title: '⚠️ Error de Correo',
                message: `No se pudo enviar correo a ${patient.email}: ${result.error || 'Error desconocido'}`,
                relatedId: formData.patientId
              });
            }
          }).catch(err => {
            logger.error('Error enviando email de cita creada:', err);
            if (emailNotificationService.isEnabled()) {
              addNotification({
                type: 'appointment_cancelled',
                title: '⚠️ Error de Correo',
                message: `No se pudo enviar correo a ${patient.email}`,
                relatedId: formData.patientId
              });
            }
          });
        }
        setIsFormOpen(false);
      }
    });
  };
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAppointment) return;
    const validation = validateAppointmentForm(formData, {
      allowPastDate: true
    });
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const selectedDoctor = getDoctor(formData.doctorId);
    if (!overrideRestrictions && selectedDoctor && selectedDoctor.schedule) {
      const dayKey = getDayOfWeekKey(formData.date) as keyof typeof selectedDoctor.schedule;
      const daySchedule = selectedDoctor.schedule[dayKey];
      const dayName = getDayOfWeekName(formData.date);
      const prefix = getDoctorPrefix(selectedDoctor.gender, getSpecialty(selectedDoctor.specialtyId)?.name);
      if (!daySchedule) {
        setFormErrors({
          date: `${prefix} ${selectedDoctor.name} no trabaja los ${dayName}`
        });
        return;
      }
      const requestedMinutes = parseInt(formData.time.split(':')[0]) * 60 + parseInt(formData.time.split(':')[1]);
      const startMinutes = parseInt(daySchedule.start.split(':')[0]) * 60 + parseInt(daySchedule.start.split(':')[1]);
      const endMinutes = parseInt(daySchedule.end.split(':')[0]) * 60 + parseInt(daySchedule.end.split(':')[1]);
      if (requestedMinutes < startMinutes || requestedMinutes >= endMinutes) {
        setFormErrors({
          time: `Fuera del horario. ${dayName}: ${formatTime(daySchedule.start)} - ${formatTime(daySchedule.end)}`
        });
        return;
      }
    }
    if (!overrideRestrictions && !checkDoctorAvailability(formData.doctorId, formData.date, formData.time, editingAppointment.id)) {
      setFormErrors({
        time: 'El doctor ya tiene una cita en este horario o dentro del margen de 30 minutos entre citas'
      });
      return;
    }
    const patient = getPatient(formData.patientId);
    const doctor = getDoctor(formData.doctorId);
    const selectedServices = services.filter(s => formData.serviceIds.includes(s.id));
    const serviceNames = selectedServices.map(s => s.name).join(', ');
    const duration = selectedServices.reduce((sum, s) => sum + s.duration, 0) || 30;
    confirm({
      title: 'Guardar Cambios',
      message: '¿Estás seguro de que deseas guardar los cambios en esta cita?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        await updateAppointment(editingAppointment.id, {
          patientId: formData.patientId,
          doctorId: formData.doctorId,
          serviceId: formData.serviceIds[0],
          serviceIds: formData.serviceIds,
          serviceDiscounts: buildServiceDiscounts(formData.serviceIds, formData.date),
          date: formData.date,
          time: formData.time,
          duration,
          notes: formData.notes
        }, overrideRestrictions);
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'update',
          module: 'appointments',
          entityId: editingAppointment.id,
          entityName: patient?.name || 'Paciente',
          details: `Cita editada: ${patient?.name || 'Paciente'} con ${doctor?.name || 'Doctor'} — ${formData.date} ${formData.time} — Servicios: ${serviceNames}`
        });
        addNotification({
          type: 'appointment_rescheduled',
          title: 'Cita Actualizada',
          message: `La cita de ${patient?.name || 'Paciente'} ha sido actualizada.`,
          relatedId: editingAppointment.id
        });
        if (patient && doctor && selectedServices.length > 0) {
          emailNotificationService.sendAppointmentRescheduled({
            patientName: patient.name,
            patientEmail: patient.email,
            doctorName: doctor.name,
            serviceName: serviceNames,
            date: formData.date,
            time: formData.time,
            duration,
            oldDate: editingAppointment.date,
            oldTime: editingAppointment.time
          }).catch(err => logger.error('Error enviando email de cita actualizada:', err));
        }
        setIsEditOpen(false);
        setEditingAppointment(null);
      }
    });
  };
  const handleCancel = () => {
    if (selectedAppointment) {
      const patient = getPatient(selectedAppointment.patientId);
      const doctor = getDoctor(selectedAppointment.doctorId);
      const service = getService(selectedAppointment.serviceId);
      cancelAppointment(selectedAppointment.id, cancelReason);
      addNotification({
        type: 'appointment_cancelled',
        title: 'Cita Cancelada',
        message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} del ${selectedAppointment.date} fue cancelada. Motivo: ${cancelReason || 'No especificado'}`,
        relatedId: selectedAppointment.id
      });
      if (patient && doctor && service) {
        emailNotificationService.sendAppointmentCancelled({
          patientName: patient.name,
          patientEmail: patient.email,
          doctorName: doctor.name,
          serviceName: service.name,
          date: selectedAppointment.date,
          time: selectedAppointment.time,
          duration: selectedAppointment.duration,
          cancelReason
        }).then(result => {
          if (result.success) {
            addNotification({
              type: 'appointment_created',
              title: '📧 Correo Enviado',
              message: `Notificación de cancelación enviada a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          } else if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de cancelación a ${patient.email}: ${result.error || 'Error desconocido'}`,
              relatedId: selectedAppointment.id
            });
          }
        }).catch(err => {
          logger.error('Error enviando email de cita cancelada:', err);
          if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de cancelación a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          }
        });
      }
      setIsCancelOpen(false);
    }
  };
  const handleReschedule = () => {
    if (selectedAppointment) {
      if (!overrideRestrictions && !checkDoctorAvailability(selectedAppointment.doctorId, rescheduleData.date, rescheduleData.time, selectedAppointment.id)) {
        showToast({
          type: 'warning',
          title: 'Horario no disponible',
          message: 'El doctor no está disponible en este horario. Recuerda que hay un margen de 30 minutos entre citas.'
        });
        return;
      }
      const patient = getPatient(selectedAppointment.patientId);
      const doctor = getDoctor(selectedAppointment.doctorId);
      const service = getService(selectedAppointment.serviceId);
      confirm({
        title: 'Reprogramar Cita',
        message: '¿Estás seguro de que deseas reprogramar esta cita?',
        confirmText: 'Reprogramar',
        onConfirm: async () => {
          try {
            await rescheduleAppointment(selectedAppointment.id, rescheduleData.date, rescheduleData.time, overrideRestrictions);
          } catch (err: any) {
            logger.error('Error reprogramando cita:', err);
            showToast({
              type: 'error',
              title: 'Error al reprogramar',
              message: err?.message || 'No se pudo reprogramar la cita. Intenta de nuevo.'
            });
            addNotification({
              type: 'appointment_cancelled',
              title: 'Error al reprogramar',
              message: err?.message || 'No se pudo reprogramar la cita. Intenta de nuevo.',
              relatedId: selectedAppointment.id
            });
            return;
          }
          addNotification({
            type: 'appointment_rescheduled',
            title: 'Cita Reprogramada',
            message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} fue reprogramada de ${selectedAppointment.date} ${formatTime(selectedAppointment.time)} a ${rescheduleData.date} ${formatTime(rescheduleData.time)}`,
            relatedId: selectedAppointment.id
          });
          if (patient && doctor && service) {
            emailNotificationService.sendAppointmentRescheduled({
              patientName: patient.name,
              patientEmail: patient.email,
              doctorName: doctor.name,
              serviceName: service.name,
              date: rescheduleData.date,
              time: rescheduleData.time,
              duration: selectedAppointment.duration,
              oldDate: selectedAppointment.date,
              oldTime: selectedAppointment.time
            }).then(result => {
              if (result.success) {
                addNotification({
                  type: 'appointment_created',
                  title: '📧 Correo Enviado',
                  message: `Notificación de reprogramación enviada a ${patient.email}`,
                  relatedId: selectedAppointment.id
                });
              } else if (emailNotificationService.isEnabled()) {
                addNotification({
                  type: 'appointment_cancelled',
                  title: '⚠️ Error de Correo',
                  message: `No se pudo enviar correo de reprogramación a ${patient.email}: ${result.error || 'Error desconocido'}`,
                  relatedId: selectedAppointment.id
                });
              }
            }).catch(err => {
              logger.error('Error enviando email de cita reprogramada:', err);
              if (emailNotificationService.isEnabled()) {
                addNotification({
                  type: 'appointment_cancelled',
                  title: '⚠️ Error de Correo',
                  message: `No se pudo enviar correo de reprogramación a ${patient.email}`,
                  relatedId: selectedAppointment.id
                });
              }
            });
          }
          setIsRescheduleOpen(false);
        }
      });
    }
  };
  const handleComplete = async () => {
    // Un canje de tarjeta de fidelidad no genera factura, así que en ese caso
    // el número deja de ser obligatorio. Aplica aunque la cita incluya otros
    // servicios que sí se cobren.
    if (!loyaltyCardToRedeem && (!completeData.invoiceNumber || completeData.invoiceNumber.trim() === '')) {
      showToast({
        type: 'error',
        title: 'Número de factura requerido',
        message: 'Debes ingresar el número de factura para completar la cita.'
      });
      return;
    }
    if (selectedAppointment) {
      confirm({
        title: 'Completar Cita',
        message: '¿Estás seguro de que deseas marcar esta cita como completada?',
        confirmText: 'Completar',
        onConfirm: async () => {
          try {
            const patient = getPatient(selectedAppointment.patientId);
            const doctor = getDoctor(selectedAppointment.doctorId);
            const serviceIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
            const selectedServices = services.filter(s => serviceIds.includes(s.id));
            const serviceNames = selectedServices.map(s => s.name).join(', ');
            await completeAppointment(selectedAppointment.id, completeData.nextAppointmentDate, completeData.nextAppointmentNotes, loyaltyCardToRedeem || undefined, loyaltyCardToRedeem ? loyaltyCards.find(card => card.id === loyaltyCardToRedeem)?.service_ids.find(id => serviceIds.includes(id)) : undefined,
            // Si se canjeó fidelidad no se guarda factura, aunque el usuario
            // hubiera escrito un número antes de marcar la tarjeta.
            loyaltyCardToRedeem ? undefined : completeData.invoiceNumber || undefined);
            // Si se eligió canjear un certificado de regalo, vincularlo a esta cita.
            // Solo aplica si coincide la persona y el servicio (garantizado por el
            // filtro que arma la lista de certificados canjeables).
            if (certToRedeem) {
              try {
                // Determinar qué servicio del certificado corresponde a esta cita
                // (el primero de la cita que tenga un servicio pendiente en el cert).
                const activeCerts = getActiveGiftCertificates(selectedAppointment.patientId);
                const cert = activeCerts.find(c => c.id === certToRedeem);
                let redeemServiceId: string | undefined;
                if (cert) {
                  const items = cert.items && cert.items.length > 0 ? cert.items : undefined;
                  if (items) {
                    const match = items.find(it => !it.redeemed && serviceIds.includes(it.serviceId));
                    redeemServiceId = match?.serviceId;
                  } else {
                    redeemServiceId = cert.serviceId;
                  }
                }
                await redeemGiftCertificate(selectedAppointment.patientId, certToRedeem, selectedAppointment.id, redeemServiceId);
                showToast({
                  type: 'success',
                  title: 'Certificado canjeado',
                  message: 'El certificado de regalo quedó vinculado a esta cita.'
                });
              } catch (certErr) {
                logger.error('Error canjeando certificado:', certErr);
              }
            }
            // Canje de fidelidad: la tarjeta ya fue filtrada por paciente y
            // sólo llega aquí si uno de sus 3 servicios coincide con la cita.
            if (loyaltyCardToRedeem) {
              const card = loyaltyCards.find(item => item.id === loyaltyCardToRedeem);
              if (card && card.service_ids.some(id => serviceIds.includes(id))) {
                try {
                  const {
                    data: loyaltyData,
                    error: loyaltyError
                  } = await supabase.from('loyalty_cards').update({
                    status: 'canjeada',
                    redeemed_at: getToday(),
                    redeemed_appointment_id: selectedAppointment.id
                  }).eq('id', card.id).eq('status', 'activa').select();
                  if (loyaltyError) throw loyaltyError;
                  if (!loyaltyData || loyaltyData.length === 0) {
                    throw new Error('La tarjeta ya no estaba activa (pudo haber sido canjeada por otro usuario o faltan permisos en Supabase).');
                  }
                  showToast({
                    type: 'success',
                    title: 'Tarjeta canjeada',
                    message: `La tarjeta ${card.code} fue canjeada por el servicio gratuito.`
                  });
                } catch (loyaltyErr) {
                  logger.error('Error canjeando tarjeta de fidelidad:', loyaltyErr);
                  showToast({
                    type: 'error',
                    title: 'No se pudo canjear la tarjeta',
                    message: `La cita se completó, pero la tarjeta ${card.code} NO fue canjeada. Verifica su estado e inténtalo de nuevo desde el módulo de Fidelidad.`
                  });
                }
              }
            }
            if (completeData.nextAppointmentDate && completeData.nextServiceIds.length > 0) {
              setTimeout(() => {
                const followUpApts = appointments.filter(a => a.patientId === selectedAppointment.patientId && a.date === completeData.nextAppointmentDate && a.status === 'programada');
                const followUp = followUpApts[followUpApts.length - 1];
                if (followUp) {
                  const nextServices = services.filter(s => completeData.nextServiceIds.includes(s.id));
                  const nextDuration = nextServices.reduce((sum, s) => sum + s.duration, 0) || 30;
                  updateAppointment(followUp.id, {
                    serviceId: completeData.nextServiceIds[0],
                    serviceIds: completeData.nextServiceIds,
                    time: completeData.nextAppointmentTime || followUp.time,
                    duration: nextDuration
                  });
                }
              }, 500);
            }
            const nextInfo = completeData.nextAppointmentDate ? ` — Próxima cita: ${completeData.nextAppointmentDate}` : '';
            addNotification({
              type: 'appointment_created',
              title: 'Cita Completada',
              message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} (${serviceNames}) fue completada exitosamente.${nextInfo}`,
              relatedId: selectedAppointment.id
            });
            sendDesktopNotification({
              title: '✅ Cita Completada',
              body: `${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'}\n${serviceNames}${nextInfo}`,
              tag: `appointment-completed-${selectedAppointment.id}`
            });
            setIsCompleteOpen(false);
          } catch (error) {
            logger.error('Error completando cita:', error);
            showToast({
              type: 'error',
              title: 'Error',
              message: 'Error al completar la cita. Intente nuevamente.'
            });
          }
        }
      });
    }
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null, exportStatus: string | null, exportPatientId: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = appointments;
    if (dateFrom && dateTo) {
      dataToExport = dataToExport.filter(a => a.date >= dateFrom && a.date <= dateTo);
    }
    if (exportStatus) {
      dataToExport = dataToExport.filter(a => a.status === exportStatus);
    }
    if (exportPatientId) {
      dataToExport = dataToExport.filter(a => a.patientId === exportPatientId);
    }
    exportAppointmentsToExcel(dataToExport, (patientId: string) => getPatient(patientId)?.name || '-', (doctorId: string) => getDoctor(doctorId)?.name || '-', (serviceId: string) => getService(serviceId)?.name || '-', (serviceId: string) => getService(serviceId)?.price || 0,
    // Misma fuente de verdad que la tabla en pantalla: incluye descuento de
    // paquete de sesiones (aunque el paquete se haya creado DESPUÉS de la
    // cita), override manual y ofertas revalidadas en vivo.
    (apt, serviceId) => getStoredOfferInfo(apt, serviceId).discountPercent ?? 0);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null, exportStatus: string | null, exportPatientId: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = appointments;
    if (dateFrom && dateTo) {
      dataToExport = dataToExport.filter(a => a.date >= dateFrom && a.date <= dateTo);
    }
    if (exportStatus) {
      dataToExport = dataToExport.filter(a => a.status === exportStatus);
    }
    if (exportPatientId) {
      dataToExport = dataToExport.filter(a => a.patientId === exportPatientId);
    }
    exportAppointmentsToPdf(dataToExport, (patientId: string) => getPatient(patientId)?.name || '-', (doctorId: string) => getDoctor(doctorId)?.name || '-', (serviceId: string) => getService(serviceId)?.name || '-', (serviceId: string) => getService(serviceId)?.price || 0, (apt, serviceId) => getStoredOfferInfo(apt, serviceId).discountPercent ?? 0);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const viewTabs = [{
    id: 'list',
    label: 'Lista',
    icon: <ListIcon className="w-4 h-4" data-id="element-299" data-mp="iejv9tb3jlvh" />
  }, {
    id: 'calendar',
    label: 'Calendario',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-300" data-mp="3lztox3lhmg5" />
  }];
  const renderAppointmentForm = (isEdit = false) => <div className="space-y-6" data-id="element-301" data-mp="8kksqt742030">
      <FormSection title="Paciente" data-id="element-302" data-mp="3qs0k339vb8ma">
        <Select label="Seleccionar Paciente" options={patientOptions} value={formData.patientId} onChange={value => setFormData({
        ...formData,
        patientId: value
      })} error={formErrors.patientId} searchable placeholder="Buscar paciente..." data-id="element-303" data-mp="3qt24vzgor9h7" />
      
      </FormSection>
      <FormSection title="Doctor" data-id="element-304" data-mp="55b5n73a5alk">
        {isOwnOnly && linkedDoctor && !isEdit ? <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4" data-id="element-305" data-mp="14r842eso4yw">
            <p className="text-sm text-teal-700 dark:text-teal-400 font-medium" data-id="element-306" data-mp="3sb23bhx4ibib">
              Doctor asignado automáticamente
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1" data-id="element-307" data-mp="3nhz9d8ofp01l">
              {getDoctorPrefix(linkedDoctor.gender, getSpecialty(linkedDoctor.specialtyId)?.name)}{' '}
              {linkedDoctor.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-308" data-mp="39izn27oe8x8l">
              {getSpecialty(linkedDoctor.specialtyId)?.name || ''}
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-309" data-mp="1k3ox21y860eq">
            <Select label="Especialidad (opcional)" options={specialtyOptions} value={selectedSpecialty} onChange={value => {
          setSelectedSpecialty(value);
          setFormData({
            ...formData,
            doctorId: ''
          });
        }} data-id="element-310" data-mp="1k5i3bg4xeim7" />
        

            <Select label="Doctor" options={doctorOptions} value={formData.doctorId} onChange={value => {
          if (value) {
            const selectedDoc = doctors.find(d => d.id === value);
            if (selectedDoc) setSelectedSpecialty(selectedDoc.specialtyId);
          }
          setFormData({
            ...formData,
            doctorId: value
          });
        }} error={formErrors.doctorId} searchable placeholder="Seleccionar doctor..." data-id="element-311" data-mp="11d7hkz2el1bb" />
        
          </div>}
      </FormSection>
      <FormSection title="Servicios" data-id="element-312" data-mp="3siqes7wgqin4">
        <div className="space-y-3" data-id="element-313" data-mp="3sjrwuwbwc5ec">
          <div className="relative" data-id="element-314" data-mp="1cyrrhdzfm4j">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-315" data-mp="1bw5jmrlu7uy" />
            <input type="text" placeholder="Buscar servicios..." value={serviceSearchTerm} onChange={e => setServiceSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-316" data-mp="3ji215eazekar" />
          
          </div>
          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/50" data-id="element-317" data-mp="34t5unlokpfc9">
            {filteredFormServices.length === 0 ? <p className="text-sm text-gray-500 italic p-2" data-id="element-318" data-mp="2vtiut4bhshlb">
                {serviceSearchTerm ? 'No se encontraron servicios con ese nombre' : 'No hay servicios activos disponibles'}
              </p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-319" data-mp="2795z083zkoun">
                {filteredFormServices.map(service => {
              const offerInfo = getServiceOfferInfo(service.id);
              return <label key={service.id} className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${formData.serviceIds.includes(service.id) ? 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800' : 'hover:bg-white dark:hover:bg-gray-800 border border-transparent'}`} data-id="element-320" data-mp="2h4plk3wnub6q">
                  
                      <input type="checkbox" checked={formData.serviceIds.includes(service.id)} onChange={() => toggleService(service.id)} className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500" data-id="element-321" data-mp="2zxthxx19j251" />
                  

                      <div className="flex-1 min-w-0" data-id="element-322" data-mp="3gzpkkl7sjewt">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate block" data-id="element-323" data-mp="3lwpoaph645kr">
                          {service.name}
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-324" data-mp="3vn25q8torimo">
                          {service.duration} min
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0" data-id="element-325" data-mp="3lchq4uyepmv">
                        {offerInfo.discountAmount > 0 ? <>
                            <p className="text-xs font-semibold text-teal-600 dark:text-teal-400" data-id="element-326" data-mp="dh5w7ts8gt7a">
                              {formatCurrency(offerInfo.finalPrice)}
                            </p>
                            <p className="text-[10px] text-gray-400 line-through" data-id="element-327" data-mp="2f9u6gn3aogwk">
                              {formatCurrency(service.price || 0)}
                            </p>
                            {offerInfo.isPackageDiscount ? <p className="text-[10px] text-purple-500 dark:text-purple-400 font-medium" data-id="element-328" data-mp="11e2afano5k5o">
                                Paquete -{offerInfo.discountPercent}%
                              </p> : <p className="text-[10px] text-emerald-500 dark:text-emerald-400 font-medium" data-id="element-329" data-mp="sictpex61dym">
                                Oferta -{offerInfo.discountPercent}%
                              </p>}
                          </> : <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-330" data-mp="1gb32xgdopj57">
                            {formatCurrency(service.price || 0)}
                          </p>}
                      </div>
                    </label>;
            })}
              </div>}
          </div>
          {formErrors.serviceId && <p className="text-sm text-red-500" data-id="element-331" data-mp="3vz36qdr60r9t">{formErrors.serviceId}</p>}
          {formData.serviceIds.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-332" data-mp="1itmywtqfr6mj">
              {selectedServicesList.map(s => <span key={s.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200" data-id="element-333" data-mp="1njv1ssllvz0v">
            
                  {s.name}
                  <button type="button" onClick={() => toggleService(s.id)} className="ml-1 hover:text-teal-900 dark:hover:text-teal-100" data-id="element-334" data-mp="1esb5ed4fy192">
              
                    <XIcon className="w-3 h-3" data-id="element-335" data-mp="26ifo87cjyg86" />
                  </button>
                </span>)}
            </div>}
          <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-336" data-mp="1slkai1lcw0ls">
            <ClockIcon className="w-4 h-4 text-gray-500" data-id="element-337" data-mp="1xk42ojqjm8ml" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-338" data-mp="h39k9ms8v88k">
              Duración Total: {totalDuration} min
            </span>
          </div>
          <div className="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800 rounded-lg space-y-1" data-id="element-339" data-mp="ej3ed9k2l7pd">
            <div className="flex items-center justify-between gap-3 text-sm" data-id="element-340" data-mp="9jqjc1j1oubp">
              <span className="text-gray-600 dark:text-gray-300" data-id="element-341" data-mp="nkjgku0qe3ii">Subtotal</span>
              <span className="font-medium text-gray-900 dark:text-white tabular-nums" data-id="element-342" data-mp="ilg9l416xn7w">
                {formatCurrency(totalPrice)}
              </span>
            </div>
            {totalDiscount > 0 && <div className="flex items-center justify-between gap-3 text-sm" data-id="element-343" data-mp="3mzodlymwgmfa">
                <span className="text-teal-700 dark:text-teal-300" data-id="element-344" data-mp="4lf7jl96w9qu">
                  Descuento de ofertas
                </span>
                <span className="font-semibold text-teal-700 dark:text-teal-300 tabular-nums" data-id="element-345" data-mp="3vzupxh6v3gcv">
                  -{formatCurrency(totalDiscount)}
                </span>
              </div>}
            <div className="flex items-center justify-between gap-3 pt-1 border-t border-teal-200/70 dark:border-teal-800" data-id="element-346" data-mp="33c7a0fgjddwk">
              <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-347" data-mp="177hplemrfmqs">
                Total a pagar
              </span>
              <span className="text-base font-bold text-teal-700 dark:text-teal-300 tabular-nums" data-id="element-348" data-mp="1c513uejgjziq">
                {formatCurrency(totalWithOffers)}
              </span>
            </div>
          </div>
        </div>
      </FormSection>
      <FormSection title="Fecha y Hora" data-id="element-349" data-mp="o9ygkb0ay44d">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-350" data-mp="ob9h81ygavr9">
          <Input type="date" label="Fecha" value={formData.date} onChange={e => setFormData({
          ...formData,
          date: e.target.value
        })} error={formErrors.date} data-id="element-351" data-mp="t7q6x0lati6g" />
        

          <Input type="time" label="Hora" value={formData.time} onChange={e => setFormData({
          ...formData,
          time: e.target.value
        })} error={formErrors.time} data-id="element-352" data-mp="29n1faoojb3r5" />
        
        </div>
        <button type="button" onClick={() => {
        setOverrideRestrictions(v => !v);
        setFormErrors({});
      }} className={`mt-3 w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${overrideRestrictions ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600/60' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-353" data-mp="3u4sgj0kdq5et">
        
          <span className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-md border-2 flex-shrink-0 transition-colors ${overrideRestrictions ? 'bg-amber-500 border-amber-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-354" data-mp="3aj5vwq5q8exj">
          
            {overrideRestrictions && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-355" data-mp="14n35952te8ua" />}
          </span>
          <span className="min-w-0" data-id="element-356" data-mp="zpb4rmo3teeh">
            <span className="block text-sm font-semibold text-gray-900 dark:text-white" data-id="element-357" data-mp="8jfhq4h4zr4g">
              Agendar sin restricciones
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-358" data-mp="dh7cr7d2vtcv">
              Ignora el horario del doctor y permite citas que se solapan o caen
              dentro del margen entre citas. Úsalo solo cuando necesites forzar
              una cita.
            </span>
          </span>
        </button>
        {overrideRestrictions && <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400" data-id="element-359" data-mp="3pzxcnf4rx4yx">
            <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-360" data-mp="3c0nysm41imy4" />
            Validaciones de horario y disponibilidad desactivadas para esta
            cita.
          </p>}
      </FormSection>
      <FormSection title="Notas" data-id="element-361" data-mp="1a917skk5es1n">
        <Textarea label="Notas Adicionales" value={formData.notes} onChange={e => setFormData({
        ...formData,
        notes: e.target.value
      })} rows={3} placeholder="Detalles adicionales sobre la cita..." data-id="element-362" data-mp="1aa2rfdf8cmuz" />
      
      </FormSection>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 modal-actions" data-id="element-363" data-mp="2usdc5agdikys">
        <Button variant="outline" type="button" onClick={() => isEdit ? setIsEditOpen(false) : setIsFormOpen(false)} data-id="element-364" data-mp="39hszshklyaa9">
        
          Cancelar
        </Button>
        <Button type="submit" data-id="element-365" data-mp="3ietmmjs140bl">
          {isEdit ? 'Guardar Cambios' : 'Crear Cita'}
        </Button>
      </div>
    </div>;
  useEffect(() => {
    if (initialDateFilter) setFilterDate(initialDateFilter);
  }, [initialDateFilter]);
  // ─── Status maps ─────────────────────────────────────────────────────────
  const accentMap: Record<string, string> = {
    programada: '#0d9488',
    completada: '#059669',
    cancelada: '#e11d48',
    reprogramada: '#d97706'
  };
  const statusLabelMap: Record<string, string> = {
    programada: 'Programada',
    completada: 'Completada',
    cancelada: 'Cancelada',
    reprogramada: 'Reprogramada'
  };
  return <div className="space-y-6" data-id="element-366" data-mp="3h88i5mf775nr">
      {/* ══════════════════════════════════════════════════════
                                       HEADER — tono frío teal profesional
                                      ══════════════════════════════════════════════════════ */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg" style={{
      background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 45%, #0891b2 100%)'
    }} data-id="element-367" data-mp="3qzurfd7z6unv">
        
        {/* Glow blobs */}
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)'
      }} data-id="element-368" data-mp="3d751ixpbt5lh" />
        

        <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full blur-3xl translate-y-1/2 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(8,145,178,0.3) 0%, transparent 70%)'
      }} data-id="element-369" data-mp="nhkveg0xqs77" />
        

        {/* Subtle grid texture */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 39px,rgba(255,255,255,1) 39px,rgba(255,255,255,1) 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,rgba(255,255,255,1) 39px,rgba(255,255,255,1) 40px)'
      }} data-id="element-370" data-mp="m0nu0v54o50s" />
        

        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-white/20" data-id="element-371" data-mp="14we0ovo09pl0" />

        <div className="relative z-10 p-6 pl-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-372" data-mp="18y90fvqafuwo">
          <div data-id="element-373" data-mp="1wnjw4yss6z3y">
            <div className="flex items-center gap-3 mb-1.5" data-id="element-374" data-mp="21gcyjg0s1vcv">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-375" data-mp="21hxvfcakdqdw">
                <CalendarIcon className="w-6 h-6 text-white" data-id="element-376" data-mp="2bbk5ni72ng9w" />
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight" data-id="element-377" data-mp="27c2xui46a71k">
                Citas
              </h1>
              <HelpTooltip moduleId="appointments" data-id="element-378" data-mp="229u4hkne12yx" />
            </div>
            <p className="text-teal-100/80 font-medium text-sm" data-id="element-379" data-mp="1xbiv1b108uh8">
              {isOwnOnly && linkedDoctor ? `Citas de ${getDoctorPrefix(linkedDoctor.gender, getSpecialty(linkedDoctor.specialtyId)?.name)} ${linkedDoctor.name}` : 'Gestión de citas médicas'}
              {isReadOnlyAppointments && <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-white/15 text-white/90" data-id="element-380" data-mp="1jlfduygzfqyn">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-381" data-mp="19m7yk5qotm8w">
            {canExportPdf && <Button variant="outline" leftIcon={<ClockIcon className="w-4 h-4" data-id="element-383" data-mp="ukho7iayp3ae" />} onClick={() => setIsPdfExportModalOpen(true)} style={{
            background: 'rgba(255,255,255,0.12)',
            borderColor: 'rgba(255,255,255,0.25)',
            color: 'white',
            backdropFilter: 'blur(8px)'
          }} data-id="element-382" data-mp="14p7tny5kqpjd">
              
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-385" data-mp="grs1fbnlzuc8" />} onClick={() => setIsExportModalOpen(true)} style={{
            background: 'rgba(255,255,255,0.12)',
            borderColor: 'rgba(255,255,255,0.25)',
            color: 'white',
            backdropFilter: 'blur(8px)'
          }} data-id="element-384" data-mp="bv1natvd78tg">
              
                Exportar Excel
              </Button>}
            {canCreateAppointments && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-387" data-mp="1zqzo5kybt18b" />} onClick={openNewForm} style={{
            background: 'white',
            color: '#0f766e'
          }} className="font-semibold shadow-lg shadow-black/20 hover:shadow-xl transition-shadow" data-id="element-386" data-mp="3u5d74cr7adts">
              
                Nueva Cita
              </Button>}
          </div>
        </div>
      </div>

      {isReadOnlyAppointments && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-388" data-mp="2ig5z2lsvoojk">
          <LockIcon className="w-4 h-4" data-id="element-389" data-mp="2seltaxn7cdxa" />
          <span data-id="element-390" data-mp="2sdkfilesxdbv">
            Solo tienes permisos de lectura. No puedes crear, editar o cancelar
            citas.
          </span>
        </div>}

      {hasOwnButNoDoctor && <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800" data-id="element-391" data-mp="y25pe3hvpbvl">
          <div className="flex items-center gap-3 p-1" data-id="element-392" data-mp="t4nh8fbor0on">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0" data-id="element-393" data-mp="34pwb21pr9j73">
              <ClockIcon className="w-5 h-5 text-white" data-id="element-394" data-mp="39o7muhzm23ry" />
            </div>
            <div data-id="element-395" data-mp="2v0wboh7cclsd">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-396" data-mp="2uzbgqqh470tz">
                Doctor no vinculado
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-400" data-id="element-397" data-mp="2zxcztbejj05g">
                Tu cuenta ({user?.email}) no está vinculada a ningún registro de
                doctor. Contacta al administrador.
              </p>
            </div>
          </div>
        </Card>}

      {/* ══════════════════════════════════════════════════════
                                       FILTROS — card blanca, separada del header
                                      ══════════════════════════════════════════════════════ */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4" data-id="element-398" data-mp="f8ymssnq2c18">
        <div className="flex flex-col gap-4" data-id="element-399" data-mp="dsbdr20fm83o">
          {/* View tabs + search row */}
          <div className="flex flex-col gap-4" data-id="element-400" data-mp="8sfa53vncion">
            <div className="flex flex-wrap gap-1.5" data-id="element-401" data-mp="8rnclnihoavq">
              {viewTabs.map(tab => <button key={tab.id} onClick={() => setActiveView(tab.id as 'list' | 'calendar')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeView === tab.id ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`} data-id="element-402" data-mp="3udqagqecfc4">
                
                  {tab.icon}
                  {tab.label}
                </button>)}
            </div>
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-403" data-mp="1f8sfyl4mga7c">
              <div className="flex-1 relative" data-id="element-404" data-mp="1fb53rex29esb">
                <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-405" data-mp="1ai27gz0wfr8f" />
                <Input style={{
                paddingLeft: '2.5rem'
              }} placeholder="Buscar por paciente o doctor..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-406" data-mp="2t6mxc4aggbwm" />
                
              </div>
              {!isOwnOnly && <div className="w-full sm:w-56" data-id="element-407" data-mp="3c3xyxvjk7hf8">
                  <Select options={doctorFilterOptions} value={filterDoctorId} onChange={setFilterDoctorId} placeholder="Doctor" searchable data-id="element-408" data-mp="3c34u5045v1zq" />
                
                </div>}
              <div className="w-full sm:w-48" data-id="element-409" data-mp="3pu9r1ug59g6d">
                <Select options={statusOptions} value={filterStatus} onChange={setFilterStatus} placeholder="Estado" data-id="element-410" data-mp="3px5ozirzs4ut" />
                
              </div>
            </div>
          </div>

          {/* Date filters row */}
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-411" data-mp="3asg0yhtld0a4">
            <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mr-1" data-id="element-412" data-mp="3jr1hk7fi6589">
              Fecha:
            </span>
            {[{
            label: 'Todas',
            value: ''
          }, {
            label: 'Hoy',
            value: today,
            icon: true
          }, {
            label: 'Mañana',
            value: getTomorrow()
          }].map(opt => {
            const isActive = filterDate === opt.value && (opt.value !== '' || !filterDate);
            const isAllActive = opt.value === '' && !filterDate;
            return <button key={opt.label} onClick={() => setFilterDate(opt.value)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${isAllActive || opt.value !== '' && filterDate === opt.value ? 'bg-teal-600 text-white shadow-sm shadow-teal-600/25' : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-413" data-mp="289icsam9xu7l">
                  
                  {opt.icon && <CalendarIcon className="w-3.5 h-3.5" data-id="element-414" data-mp="2cx3swq7qulyj" />}
                  {opt.label}
                </button>;
          })}

            {/* Custom date picker */}
            <div className="relative" ref={pickerRef} data-id="element-415" data-mp="2htueft2bi9rc">
              <button ref={pickerBtnRef} type="button" onClick={() => {
              setPickerNavDate(filterDate ? new Date(filterDate + 'T12:00:00') : new Date());
              setIsDatePickerOpen(v => !v);
            }} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${filterDate && filterDate !== today && filterDate !== getTomorrow() ? 'bg-teal-600 text-white shadow-sm shadow-teal-600/25' : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-416" data-mp="22xmn88shs82k">
                
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-417" data-mp="36jqjs7clc0sx" />
                {filterDate && filterDate !== today && filterDate !== getTomorrow() ? (() => {
                const [y, m, d] = filterDate.split('-');
                return `${d}/${m}/${y}`;
              })() : 'Elegir fecha'}
              </button>

              {isDatePickerOpen && createPortal(<>
                    <div className="fixed inset-0 z-[299] sm:bg-transparent bg-black/50" onClick={() => setIsDatePickerOpen(false)} data-id="element-418" data-mp="2nsz0oe2sww1c" />
                  

                    <div ref={pickerPopupRef} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[300] sm:translate-x-0 sm:translate-y-0" style={window.innerWidth >= 640 && pickerBtnRef.current ? {
                top: pickerBtnRef.current.getBoundingClientRect().bottom + 8,
                left: pickerBtnRef.current.getBoundingClientRect().left,
                transform: 'none'
              } : {}} data-id="element-419" data-mp="2l8av0u8r9z5h">
                    
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-4 w-72" data-id="element-420" data-mp="1nm3rzos3oev8">
                        {(() => {
                    const pYear = pickerNavDate.getFullYear();
                    const pMonth = pickerNavDate.getMonth();
                    const pDays = getDaysInMonth(pYear, pMonth);
                    const pFirstDay = getFirstDayOfMonth(pYear, pMonth);
                    const pCells: (number | null)[] = [];
                    for (let i = 0; i < pFirstDay; i++) pCells.push(null);
                    for (let i = 1; i <= pDays; i++) pCells.push(i);
                    return <>
                              <div className="flex items-center justify-between mb-3" data-id="element-421" data-mp="1npcim8fc91go">
                                <button type="button" onClick={() => setPickerNavDate(new Date(pYear, pMonth - 1, 1))} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-422" data-mp="1ro1tnucwm6x5">
                                
                                  <ChevronLeftIcon className="w-4 h-4 text-gray-500" data-id="element-423" data-mp="2kr4e4cl3zx9m" />
                                </button>
                                <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-424" data-mp="2gp9c15vctfus">
                                  {getMonthName(pMonth)} {pYear}
                                </span>
                                <button type="button" onClick={() => setPickerNavDate(new Date(pYear, pMonth + 1, 1))} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-425" data-mp="2piw34iom9uxn">
                                
                                  <ChevronRightIcon className="w-4 h-4 text-gray-500" data-id="element-426" data-mp="3n5uvwic6uiq9" />
                                </button>
                              </div>
                              <div className="grid grid-cols-7 mb-1" data-id="element-427" data-mp="3i94enrondh39">
                                {['D', 'L', 'M', 'X', 'J', 'V', 'S'].map(d => <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1" data-id="element-428" data-mp="1wpj13gmly7t1">
                                  
                                      {d}
                                    </div>)}
                              </div>
                              <div className="grid grid-cols-7 gap-0.5" data-id="element-429" data-mp="2fmtq88eizkur">
                                {pCells.map((day, idx) => {
                          if (day === null) return <div key={`e-${idx}`} data-id="element-430" data-mp="2pdy414sgszo0" />;
                          const dateStr = `${pYear}-${String(pMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const isToday = dateStr === today;
                          const isSelected = dateStr === filterDate;
                          return <button key={day} type="button" onClick={() => {
                            setFilterDate(dateStr);
                            setIsDatePickerOpen(false);
                          }} className={`aspect-square rounded-lg text-xs font-medium flex items-center justify-center transition-colors
                                      ${isSelected ? 'bg-teal-600 text-white' : ''}
                                      ${isToday && !isSelected ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 font-bold' : ''}
                                      ${!isSelected && !isToday ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}`} data-id="element-431" data-mp="hrwoikx1j13g" data-mpi={idx}>
                                    
                                      {day}
                                    </button>;
                        })}
                              </div>
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700" data-id="element-432" data-mp="289lbtnb2eq8g">
                                <button type="button" onClick={() => {
                          setFilterDate(today);
                          setIsDatePickerOpen(false);
                        }} className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:underline" data-id="element-433" data-mp="1yka5u9jh61os">
                                
                                  Ir a hoy
                                </button>
                                <button type="button" onClick={() => setIsDatePickerOpen(false)} className="text-xs text-gray-400 hover:text-gray-600" data-id="element-434" data-mp="35xbxgq0umxnn">
                                
                                  Cerrar
                                </button>
                              </div>
                            </>;
                  })()}
                      </div>
                    </div>
                  </>, document.body)}
            </div>

            {filterDate && <button onClick={() => setFilterDate('')} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-id="element-435" data-mp="1hz1jq06zll2l">
              
                <XIcon className="w-3.5 h-3.5" data-id="element-436" data-mp="1m1oaryvu31yq" /> Limpiar
              </button>}
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full" data-id="element-437" data-mp="3ss5p9fon19ln">
              {filteredAppointments.length} cita
              {filteredAppointments.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
                                       LIST VIEW — tabla rediseñada
                                      ══════════════════════════════════════════════════════ */}
      {activeView === 'list' && <div className="rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm" data-id="element-438" data-mp="3go9w6it3yw99">
          {/* Mobile cards */}
          <div className="sm:hidden" data-id="element-439" data-mp="2nzccqcd5utex">
            {filteredAppointments.length === 0 ? <div className="py-16 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-600" data-id="element-440" data-mp="2synvts102azo">
                <CalendarIcon className="w-8 h-8 opacity-40" data-id="element-441" data-mp="2xyti0soi35ad" />
                <p className="text-sm font-medium" data-id="element-442" data-mp="2xvxhcwl9q2i8">No se encontraron citas</p>
              </div> : <div className="divide-y divide-gray-50 dark:divide-gray-800/80" data-id="element-443" data-mp="8b2jbsijrg55">
                {paginatedAppointments.map(apt => {
            const patient = getPatient(apt.patientId);
            const doctor = getDoctor(apt.doctorId);
            const serviceIds = apt.serviceIds?.length > 0 ? apt.serviceIds : [apt.serviceId];
            const aptServices = services.filter(s => serviceIds.includes(s.id));
            const serviceNames = aptServices.map(s => s.name).join(', ');
            const canModify = apt.status === 'programada' || apt.status === 'reprogramada';
            const accent = accentMap[apt.status] ?? '#9ca3af';
            return <div key={apt.id} className="relative flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors" data-id="element-444" data-mp="151y7yeiibt65">
                  
                      <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full" style={{
                background: accent
              }} data-id="element-445" data-mp="2zeo0art88ubf" />
                  

                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ml-2" style={{
                background: `linear-gradient(135deg, ${accent}cc, ${accent})`
              }} data-id="element-446" data-mp="27o7gw150zcqs">
                    
                        {(patient?.name?.charAt(0) || 'P').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-447" data-mp="2qe5pcicqj7uy">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug truncate" data-id="element-448" data-mp="2qcm5khrveeof">
                          {patient?.name || '—'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-px" data-id="element-449" data-mp="2gg9ezsrmggxw">
                          {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '—'}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-id="element-450" data-mp="3gqiqfrewf9bz">
                          <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400" data-id="element-451" data-mp="3lobx0lk465cs">
                            {apt.date} · {formatTime(apt.time)}
                          </span>
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{
                    background: `${accent}18`,
                    color: accent
                  }} data-id="element-452" data-mp="3vf6llwku1h1e">
                        
                            {statusLabelMap[apt.status] ?? apt.status}
                          </span>
                        </div>
                        {serviceNames && <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5" data-id="element-453" data-mp="2f05cynsg1gzl">
                            {serviceNames}
                          </p>}
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-454" data-mp="2byioh827tiz0">
                        <button onClick={() => openDetailModal(apt)} className="p-2 rounded-lg text-gray-300 dark:text-gray-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all" title="Ver detalle" data-id="element-455" data-mp="27wxcdoxi98pv">
                      
                          <EyeIcon className="w-3.5 h-3.5" data-id="element-456" data-mp="2uvadly4fusaz" />
                        </button>
                        {canModify && <>
                            {!isReadOnlyAppointments && <button onClick={() => openEditModal(apt)} className="p-2 rounded-lg text-gray-300 dark:text-gray-600 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all" title="Editar" data-id="element-457" data-mp="34lwr9mslh8r1">
                        
                                <EditIcon className="w-3.5 h-3.5" data-id="element-458" data-mp="39joew4m7i8pf" />
                              </button>}
                            <button onClick={() => openActionSelectModal(apt)} className="p-2 rounded-lg text-gray-300 dark:text-gray-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all" title="Gestionar" data-id="element-459" data-mp="140ib1ngfq5g">
                        
                              <CheckIcon className="w-3.5 h-3.5" data-id="element-460" data-mp="39iim2r6d4jj9" />
                            </button>
                          </>}
                      </div>
                    </div>;
          })}
              </div>}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-461" data-mp="35ii5p0zpdy9k">
            <table className="w-full border-collapse" data-id="element-462" data-mp="2qkpu2jxa1u0n">
              <thead data-id="element-463" data-mp="2ql7xc0icx8i2">
                <tr className="border-b border-gray-100 dark:border-gray-800" data-id="element-464" data-mp="2qmshcw71p91o">
                  <SortableHeader label="Paciente" sortKey="patientId" currentDirection={aptIsSorted('patientId')} onSort={aptRequestSort} className="py-3.5 px-5 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-465" data-mp="2vir8grjrd55s" />
                

                  <SortableHeader label="Doctor" sortKey="doctorId" currentDirection={aptIsSorted('doctorId')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-466" data-mp="3k4isbowz6l47" />
                

                  <SortableHeader label="Servicios" sortKey="serviceId" currentDirection={aptIsSorted('serviceId')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest hidden md:table-cell" data-id="element-467" data-mp="316y0c8ywz6fd" />
                

                  <SortableHeader label="Fecha y Hora" sortKey="dateTimeSort" currentDirection={aptIsSorted('dateTimeSort')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-468" data-mp="1bxxcz6gqbjkk" />
                

                  <SortableHeader label="Estado" sortKey="status" currentDirection={aptIsSorted('status')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-469" data-mp="o22rdkcldcc8" />
                

                  <SortableHeader label="Factura" sortKey="invoiceNumber" currentDirection={aptIsSorted('invoiceNumber')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest hidden lg:table-cell" data-id="element-470" data-mp="29c9kromncl0j" />
                

                  <SortableHeader label="Creada por" sortKey="createdByUserName" currentDirection={aptIsSorted('createdByUserName')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest hidden md:table-cell" data-id="element-471" data-mp="nb49677t0qly" />
                

                  <th className="py-3.5 px-5 text-right text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-472" data-mp="4d0daegxgo23">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-473" data-mp="37yz3v8528obm">
                {filteredAppointments.length === 0 ? <tr data-id="element-474" data-mp="33yovjotspltl">
                    <td colSpan={8} className="py-16" data-id="element-475" data-mp="33xdwg2bap28t">
                      <div className="flex flex-col items-center gap-3 text-gray-300 dark:text-gray-600" data-id="element-476" data-mp="2tuibizo1l63e">
                        <CalendarIcon className="w-8 h-8" data-id="element-477" data-mp="2ynbd5eo7ryb9" />
                        <p className="text-sm font-medium text-gray-400 dark:text-gray-500" data-id="element-478" data-mp="2jxdjwkbfgif7">
                          No se encontraron citas
                        </p>
                      </div>
                    </td>
                  </tr> : paginatedAppointments.map(apt => {
              const patient = getPatient(apt.patientId);
              const doctor = getDoctor(apt.doctorId);
              const serviceIds = apt.serviceIds?.length > 0 ? apt.serviceIds : [apt.serviceId];
              const aptServices = services.filter(s => serviceIds.includes(s.id));
              const serviceNames = aptServices.map(s => s.name).join(', ');
              const canModify = apt.status === 'programada' || apt.status === 'reprogramada';
              const accent = accentMap[apt.status] ?? '#9ca3af';
              return <tr key={apt.id} className="group border-b border-gray-50 dark:border-gray-800/60 last:border-0 hover:bg-teal-50/30 dark:hover:bg-teal-900/10 transition-colors" data-id="element-479" data-mp="13ij4moxrercw">
                    
                        <td className="py-3.5 px-5" data-id="element-480" data-mp="1w6yiv992h5n6">
                          <div className="flex items-center gap-3" data-id="element-481" data-mp="1r855rhxozgnj">
                            <div className="relative flex-shrink-0" data-id="element-482" data-mp="1razpf78vsxfw">
                              <div className="absolute -left-3 top-0 bottom-0 w-[3px] rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity" style={{
                        background: accent
                      }} data-id="element-483" data-mp="1mgdo5wwikl9j" />
                          

                              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[13px] font-bold text-white" style={{
                        background: `linear-gradient(135deg, ${accent}99, ${accent})`
                      }} data-id="element-484" data-mp="2a5oeslk5gz9y">
                            
                                {(patient?.name?.charAt(0) || 'P').toUpperCase()}
                              </div>
                            </div>
                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100" data-id="element-485" data-mp="kliypji20dzv">
                              {patient?.name || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3.5 px-4" data-id="element-486" data-mp="ye8q63gcqb4d">
                          <p className="text-sm text-gray-700 dark:text-gray-200 font-medium leading-snug" data-id="element-487" data-mp="yfs3pmami82g">
                            {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '—'}
                          </p>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5" data-id="element-488" data-mp="28lg59jusbqsi">
                            {doctor && getSpecialty(doctor.specialtyId)?.name}
                          </p>
                        </td>
                        <td className="py-3.5 px-4 hidden md:table-cell" data-id="element-489" data-mp="2ncp32g8ucigq">
                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-1" data-id="element-490" data-mp="2net32n3mcxbq">
                            {serviceNames || '—'}
                          </p>
                          <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium" data-id="element-491" data-mp="2dmdkpooeu6qb">
                            {apt.duration} min
                          </span>
                        </td>
                        <td className="py-3.5 px-4" data-id="element-492" data-mp="369838g1o2w15">
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 tabular-nums" data-id="element-493" data-mp="3b29iwq9p6zls">
                            {apt.date}
                          </p>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium mt-0.5 tabular-nums flex items-center gap-1" data-id="element-494" data-mp="3gfhnsj678nab">
                            <ClockIcon className="w-3 h-3" data-id="element-495" data-mp="3u64ouhzfmp9k" />
                            {formatTime(apt.time)}
                          </p>
                        </td>
                        <td className="py-3.5 px-4" data-id="element-496" data-mp="31h77ch31owhv">
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{
                    background: `${accent}15`,
                    color: accent
                  }} data-id="element-497" data-mp="31k1tqe5mcrdx">
                        
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                      background: accent
                    }} data-id="element-498" data-mp="qpnxiujg2wy9" />
                        

                            {statusLabelMap[apt.status] ?? apt.status}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 hidden lg:table-cell" data-id="element-499" data-mp="12s7bcsfjjskt">
                          <span className="text-xs font-mono text-gray-500 dark:text-gray-400" data-id="element-500" data-mp="12t06ew550tn4">
                            {apt.invoiceNumber || '—'}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 hidden md:table-cell" data-id="element-501" data-mp="a1oxqxh6y8t5">
                          {apt.createdByUserName ? <div className="flex items-center gap-1.5" data-id="element-502" data-mp="k1zcwnmsdqay">
                              <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-503" data-mp="jzmlzkz1w5lr">
                                <UserIcon className="w-3 h-3 text-gray-400" data-id="element-504" data-mp="3nj7qgvahe3zv" />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-505" data-mp="3sghgod5p2jor">
                                {apt.createdByUserName}
                              </span>
                            </div> : <span className="text-gray-200 dark:text-gray-700" data-id="element-506" data-mp="3fzgdtlgwaqte">
                              —
                            </span>}
                        </td>
                        <td className="py-3.5 px-5" data-id="element-507" data-mp="3607ml0dezqaq">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-508" data-mp="31yvveqew6x0f">
                            <button onClick={() => openDetailModal(apt)} className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all" title="Ver detalle" data-id="element-509" data-mp="2x7njnnq3azk3">
                          
                              <EyeIcon className="w-3.5 h-3.5" data-id="element-510" data-mp="2iczi2knt8l9r" />
                            </button>
                            {canModify && <>
                                {!isReadOnlyAppointments && <button onClick={() => openEditModal(apt)} className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all" title="Editar" data-id="element-511" data-mp="37ztttici6twf">
                            
                                    <EditIcon className="w-3.5 h-3.5" data-id="element-512" data-mp="2k84yru8arrbs" />
                                  </button>}
                                <button onClick={() => openActionSelectModal(apt)} className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all" title="Gestionar cita" data-id="element-513" data-mp="2b91fap57x9wr">
                            
                                  <CheckIcon className="w-3.5 h-3.5" data-id="element-514" data-mp="digvbqlhivct" />
                                </button>
                              </>}
                          </div>
                        </td>
                      </tr>;
            })}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-gray-50 dark:border-gray-800" data-id="element-515" data-mp="230ni3hbrmatj">
            <Pagination currentPage={aptPage} totalPages={aptTotalPages} totalItems={aptTotalItems} startIndex={aptStartIndex} endIndex={aptEndIndex} onPageChange={aptGoToPage} onNext={aptNextPage} onPrev={aptPrevPage} data-id="element-516" data-mp="22ykfzyxc8r8g" />
          
          </div>
        </div>}

      {/* ── Calendar View ── */}
      {activeView === 'calendar' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-517" data-mp="wbfaiflw77hq">
          <Card className="lg:col-span-2" data-id="element-518" data-mp="wdryb9ec0c2p">
            <CardHeader data-id="element-519" data-mp="rdx8l96alsj8">
              <div className="flex items-center justify-between" data-id="element-520" data-mp="ref2hz5hfunz">
                <CardTitle data-id="element-521" data-mp="2rfwszac3hpx0">Calendario de Citas</CardTitle>
                <div className="flex items-center gap-2" data-id="element-522" data-mp="2rhgaf49hm70d">
                  <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-523" data-mp="2wezni0ug8dqq">
                  
                    <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-524" data-mp="35febj7yss6xx" />
                  </button>
                  <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white min-w-[120px] sm:min-w-[160px] text-center" data-id="element-525" data-mp="3aehgkses2d5w">
                    {getMonthName(month)} {year}
                  </span>
                  <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-526" data-mp="3k4tz6f317w9e">
                  
                    <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-527" data-mp="3t4qswv4a3lbb" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-528" data-mp="1w35m9mh9o14">
              <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-529" data-mp="1mg6thbz0zo86">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2" data-id="element-530" data-mp="1hfkcgd4f9t48">
                  
                      {day}
                    </div>)}
              </div>
              <div className="grid grid-cols-7 gap-1" data-id="element-531" data-mp="2577wcb39o3x0">
                {calendarDays.map((day, index) => {
              if (day === null) return <div key={`empty-${index}`} className="aspect-square" data-id="element-532" data-mp="217gy5mbeulig" />;
              const dateStr = getDateString(day);
              const appointmentCount = getAppointmentCountForDay(day);
              const isToday = dateStr === today;
              const isSelected = dateStr === selectedDate;
              return <button key={day} onClick={() => setSelectedDate(dateStr)} style={isToday ? {
                background: 'linear-gradient(135deg, #0f766e, #0891b2)'
              } : undefined} className={`aspect-square rounded-lg text-sm p-1 flex flex-col items-center justify-center transition-colors
                        ${isToday ? 'text-white shadow-md' : ''}
                        ${isSelected && !isToday ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 ring-2 ring-teal-500' : ''}
                        ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}`} data-id="element-533" data-mp="2k3p6x03mteii" data-mpi={index}>
                    
                      <span className="font-medium" data-id="element-534" data-mp="qra7zwko2a9e">{day}</span>
                      {appointmentCount > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full mt-0.5 ${isToday ? 'bg-white/30 text-white' : 'bg-teal-100 text-teal-600 dark:bg-teal-900 dark:text-teal-400'}`} data-id="element-535" data-mp="ut57qwmy8fl2">
                      
                          {appointmentCount}
                        </span>}
                    </button>;
            })}
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-536" data-mp="s8qpcsmkdbr7">
            <CardHeader data-id="element-537" data-mp="s64i6u3l4y9i">
              <CardTitle data-id="element-538" data-mp="4id8ly9fizbx">
                {selectedDate === today ? 'Citas de Hoy' : `Citas del ${selectedDate}`}
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-539" data-mp="e8g01u0wk585">
              {(() => {
            const visibleDateAppointments = selectedDateAppointments.filter(apt => {
              if (isOwnOnly && linkedDoctor) return apt.doctorId === linkedDoctor.id;
              if (filterDoctorId) return apt.doctorId === filterDoctorId;
              return true;
            });
            return visibleDateAppointments.length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-540" data-mp="3hl6v193v1cc9">
                    No hay citas programadas
                  </p> : <div className="space-y-3" data-id="element-541" data-mp="38k8s6u55rav9">
                    {visibleDateAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const serviceIds = apt.serviceIds?.length > 0 ? apt.serviceIds : [apt.serviceId];
                const aptServices = services.filter(s => serviceIds.includes(s.id));
                const serviceNames = aptServices.map(s => s.name).join(', ');
                const calAccent = accentMap[apt.status] ?? '#9ca3af';
                return <div key={apt.id} onClick={() => openDetailModal(apt)} className="p-3.5 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow relative cursor-pointer" style={{
                  borderLeft: `3px solid ${calAccent}`
                }} data-id="element-542" data-mp="2kstk0kqp9zc3">
                      
                          <div className="flex items-start justify-between mb-2.5" data-id="element-543" data-mp="2k77a35mnjeld">
                            <div className="flex items-center gap-2" data-id="element-544" data-mp="2k4uie0r3dpv4">
                              <div className="p-1.5 rounded-lg" style={{
                        background: `${calAccent}15`
                      }} data-id="element-545" data-mp="2f7kw2tz01ubi">
                            
                                <ClockIcon className="w-3.5 h-3.5" style={{
                          color: calAccent
                        }} data-id="element-546" data-mp="32xf859bhveup" />
                            
                              </div>
                              <div data-id="element-547" data-mp="3aye70abeqrgz">
                                <p className="font-bold text-gray-900 dark:text-white text-sm" data-id="element-548" data-mp="3axl5pov7wu68">
                                  {formatTime(apt.time)}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500" data-id="element-549" data-mp="3kopkangzed0j">
                                  {apt.duration} min
                                </p>
                              </div>
                            </div>
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{
                      background: `${calAccent}15`,
                      color: calAccent
                    }} data-id="element-550" data-mp="3tn1cgtxhxiv3">
                          
                              <span className="w-1.5 h-1.5 rounded-full" style={{
                        background: calAccent
                      }} data-id="element-551" data-mp="298ixyqlrqjnl" />
                          

                              {statusLabelMap[apt.status] ?? apt.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-1" data-id="element-552" data-mp="cmrmlj03tm6h">
                            <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{
                      background: 'linear-gradient(135deg, #0f766e, #0891b2)'
                    }} data-id="element-553" data-mp="ckooebossmqo">
                          
                              {(patient?.name?.charAt(0) || 'P').toUpperCase()}
                            </div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" data-id="element-554" data-mp="3l3mfc5n6tq6u">
                              {patient?.name}
                            </p>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 pl-8" data-id="element-555" data-mp="17spauh5lzh1l">
                            {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : ''}
                          </p>
                          {serviceNames && <p className="text-xs text-teal-600 dark:text-teal-400 mt-1.5 pl-8 truncate font-medium" data-id="element-556" data-mp="1ri9ivpdvgl8k">
                              {serviceNames}
                            </p>}
                        </div>;
              })}
                  </div>;
          })()}
            </CardContent>
          </Card>
        </div>}

      {/* ── Modals ── */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title="Detalle de la Cita" size="md" icon={<EyeIcon className="w-6 h-6" data-id="element-558" data-mp="b26kr73pzw5i" />} accentColor="sky" data-id="element-557" data-mp="64wzm3nd66nh">
        
        {selectedAppointment && (() => {
        const pt = getPatient(selectedAppointment.patientId);
        const doc = getDoctor(selectedAppointment.doctorId);
        const spec = doc ? getSpecialty(doc.specialtyId) : null;
        const sIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
        const detailServices = services.filter(s => sIds.includes(s.id));
        const detailTotalPrice = detailServices.reduce((sum, s) => sum + (s.price || 0), 0);
        const detailTotalDiscount = detailServices.reduce((sum, s) => sum + getStoredOfferInfo(selectedAppointment, s.id).discountAmount, 0);
        const detailFinalPrice = Math.max(0, detailTotalPrice - detailTotalDiscount);
        const detailAccent = accentMap[selectedAppointment.status] ?? '#9ca3af';
        return <div className="space-y-5" data-id="element-559" data-mp="1189amc5li0ra">
                {/* Header card */}
                <div className="flex items-center gap-4 p-4 rounded-2xl" style={{
            background: `${detailAccent}12`
          }} data-id="element-560" data-mp="1166gph1bnnha">
                
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0" style={{
              background: `linear-gradient(135deg, ${detailAccent}cc, ${detailAccent})`
            }} data-id="element-561" data-mp="dak5ng5u49gz">
                  
                    {(pt?.name?.charAt(0) || 'P').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-562" data-mp="1ynwz1uq244fs">
                    <p className="text-lg font-bold text-gray-900 dark:text-white truncate" data-id="element-563" data-mp="1yn548m4aaaqk">
                      {pt?.name || '—'}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-564" data-mp="3n7axa7lrzmhn">
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{
                  background: `${detailAccent}20`,
                  color: detailAccent
                }} data-id="element-565" data-mp="3n9nc4bw8lgqh">
                      
                        <span className="w-1.5 h-1.5 rounded-full" style={{
                    background: detailAccent
                  }} data-id="element-566" data-mp="34gt9mhfsvh35" />
                      

                        {statusLabelMap[selectedAppointment.status] ?? selectedAppointment.status}
                      </span>
                      {selectedAppointment.giftCertificateId && <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" data-id="element-567" data-mp="tnp1qvaofggx">
                          <GiftIcon className="w-3 h-3" data-id="element-568" data-mp="3tra06b6el7nm" />
                          Pagado con certificado
                        </span>}
                      {selectedAppointment.loyaltyCardId && <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-id="element-569" data-mp="3jzmbuj5586qd">
                          <GiftIcon className="w-3 h-3" data-id="element-570" data-mp="3axwdetcug7j3" />
                          Canjeado con tarjeta de fidelidad
                        </span>}
                    </div>
                  </div>
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-571" data-mp="35zl4qlyac32g">
                  <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-572" data-mp="za6grbyw5vux">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-573" data-mp="147yiexpc8wcb">
                      <CalendarIcon className="w-3.5 h-3.5" data-id="element-574" data-mp="2oopnn6cngnfe" />
                      Fecha
                    </div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums" data-id="element-575" data-mp="2yglkyf6ww9ik">
                      {selectedAppointment.date}
                    </p>
                  </div>
                  <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-576" data-mp="37h9xf5gnq1tc">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-577" data-mp="32hf836cj5kae">
                      <ClockIcon className="w-3.5 h-3.5" data-id="element-578" data-mp="3hdd84zpkr8rw" />
                      Hora · Duración
                    </div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums" data-id="element-579" data-mp="1n16n796vpdme">
                      {formatTime(selectedAppointment.time)} ·{' '}
                      {selectedAppointment.duration} min
                    </p>
                  </div>
                  <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-580" data-mp="3ctx8zeeyd2z7">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-581" data-mp="331rhs6wiypl2">
                      <StethoscopeIcon className="w-3.5 h-3.5" data-id="element-582" data-mp="2yylgzes6lqlz" />
                      Doctor
                    </div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-583" data-mp="2p2i3xdvsk7k9">
                      {doc ? `${getDoctorPrefix(doc.gender, spec?.name)} ${doc.name}` : '—'}
                    </p>
                    {spec?.name && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5" data-id="element-584" data-mp="2g3wovspj3ao8">
                        {spec.name}
                      </p>}
                  </div>
                  <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-585" data-mp="eb8gl4y11x1l">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-586" data-mp="2176mzue21dnf">
                      <UserIcon className="w-3.5 h-3.5" data-id="element-587" data-mp="25yeycvyy3938" />
                      Registrado por
                    </div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-588" data-mp="2a1l13tmumi4y">
                      {selectedAppointment.createdByUserName || '—'}
                    </p>
                  </div>
                </div>

                {/* Services */}
                <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-589" data-mp="1m8urvs6bycyd">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2.5" data-id="element-590" data-mp="1rak1d3dwmikc">
                    <FileTextIcon className="w-3.5 h-3.5" data-id="element-591" data-mp="32nmka53h7k7o" />
                    Servicios
                  </div>
                  {detailServices.length > 0 ? <div className="space-y-2" data-id="element-592" data-mp="12nzi8i7cfz7h">
                      {detailServices.map(s => {
                const offerInfo = getStoredOfferInfo(selectedAppointment, s.id);
                return <div key={s.id} className="flex items-center justify-between gap-3" data-id="element-593" data-mp="17livpfw7w7yd">
                        
                            <span className="text-sm text-gray-700 dark:text-gray-200" data-id="element-594" data-mp="ex3huwotnvom">
                              {s.name}
                            </span>
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0" data-id="element-595" data-mp="tn1dfxp2znnu">
                              {offerInfo.discountAmount > 0 ? <>
                                  <span className="text-teal-600 dark:text-teal-400" data-id="element-596" data-mp="3sbqdf8e3967y">
                                    {formatCurrency(offerInfo.finalPrice)}
                                  </span>
                                  <span className="block text-[10px] text-gray-400 line-through text-right" data-id="element-597" data-mp="2ckfrkm5mk09a">
                                    {formatCurrency(s.price || 0)}
                                  </span>
                                  {offerInfo.isPackageDiscount ? <span className="block text-[10px] text-purple-500 dark:text-purple-400 font-medium text-right" data-id="element-598" data-mp="2qjnthmt69mh6">
                                      Paquete -{offerInfo.discountPercent}%
                                    </span> : <span className="block text-[10px] text-emerald-500 dark:text-emerald-400 font-medium text-right" data-id="element-599" data-mp="2hhxthsl85f7s">
                                      Oferta -{offerInfo.discountPercent}%
                                    </span>}
                                </> : formatCurrency(s.price || 0)}
                            </span>
                          </div>;
              })}
                      <div className="flex items-center justify-between gap-3 pt-2 mt-1 border-t border-gray-200 dark:border-gray-600" data-id="element-600" data-mp="1lllh0k0g6zop">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-601" data-mp="1gn0hynwxmm1u">
                          Subtotal
                        </span>
                        <span className="text-sm font-bold text-gray-600 dark:text-gray-300 tabular-nums" data-id="element-602" data-mp="29bo9h189y7nu">
                          {formatCurrency(detailTotalPrice)}
                        </span>
                      </div>
                      {detailTotalDiscount > 0 && <div className="flex items-center justify-between gap-3" data-id="element-603" data-mp="1vbn71hcd2s69">
                          <span className="text-sm font-semibold text-teal-700 dark:text-teal-300" data-id="element-604" data-mp="1vdzm9mqqiofm">
                            Descuento
                          </span>
                          <span className="text-sm font-bold text-teal-700 dark:text-teal-300 tabular-nums" data-id="element-605" data-mp="juwwxn4thg7d">
                            -{formatCurrency(detailTotalDiscount)}
                          </span>
                        </div>}
                      <div className="flex items-center justify-between gap-3 pt-2 mt-1 border-t border-gray-200 dark:border-gray-600" data-id="element-606" data-mp="34jzwlr8anf24">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-607" data-mp="39c1dxrkbjvz0">
                          Total
                        </span>
                        <span className="text-sm font-bold text-teal-600 dark:text-teal-400 tabular-nums" data-id="element-608" data-mp="3j95unbveaz8f">
                          {formatCurrency(detailFinalPrice)}
                        </span>
                      </div>
                    </div> : <p className="text-sm text-gray-400 dark:text-gray-500" data-id="element-609" data-mp="3s9kjumbhcyh7">
                      Sin servicios
                    </p>}
                </div>

                {/* Notes */}
                {selectedAppointment.notes && <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-610" data-mp="316qq5z9wt4nu">
                    <div className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-611" data-mp="2w8xfasxnvmh8">
                      Notas
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed" data-id="element-612" data-mp="365tt9e6bx0t6">
                      {selectedAppointment.notes}
                    </p>
                  </div>}

                {/* Reschedule info */}
                {(selectedAppointment.originalDate || selectedAppointment.originalTime) && <div className="p-3.5 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/40" data-id="element-613" data-mp="3owkyq4n7472a">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wide mb-1.5" data-id="element-614" data-mp="6q6nidgbab7l">
                      <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-615" data-mp="1psfuwimh3a3" />
                      Reprogramada
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-200 tabular-nums" data-id="element-616" data-mp="qchyaj6tzuk5">
                      Original: {selectedAppointment.originalDate || '—'}
                      {selectedAppointment.originalTime ? ` · ${formatTime(selectedAppointment.originalTime)}` : ''}
                    </p>
                  </div>}

                {/* Cancellation reason */}
                {selectedAppointment.cancelReason && <div className="p-3.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40" data-id="element-617" data-mp="7fysa2rbqmrt">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-500 dark:text-red-400 uppercase tracking-wide mb-1.5" data-id="element-618" data-mp="3tt7422ylb08r">
                      <XIcon className="w-3.5 h-3.5" data-id="element-619" data-mp="3fuh8irrf58mi" />
                      Motivo de cancelación
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed" data-id="element-620" data-mp="3kule3mqsgixy">
                      {selectedAppointment.cancelReason}
                    </p>
                  </div>}

                {/* Next appointment */}
                {selectedAppointment.nextAppointmentDate && <div className="p-3.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/40" data-id="element-621" data-mp="3mbk5dbtbzimm">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide mb-1.5" data-id="element-622" data-mp="3hdii0eovgx5c">
                      <CalendarIcon className="w-3.5 h-3.5" data-id="element-623" data-mp="37ll615q1i15k" />
                      Próxima cita
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-200 tabular-nums" data-id="element-624" data-mp="32o2ur8ojv9ue">
                      {selectedAppointment.nextAppointmentDate}
                    </p>
                    {selectedAppointment.nextAppointmentNotes && <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1 whitespace-pre-wrap" data-id="element-625" data-mp="2tog2pdhpnge0">
                        {selectedAppointment.nextAppointmentNotes}
                      </p>}
                  </div>}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-1 modal-actions" data-id="element-626" data-mp="1i5sblg321jsp">
                  {!isReadOnlyAppointments && (selectedAppointment.status === 'programada' || selectedAppointment.status === 'reprogramada') && <Button variant="outline" leftIcon={<EditIcon className="w-4 h-4" data-id="element-628" data-mp="ud26nqtkk0rx" />} onClick={() => {
              const apt = selectedAppointment;
              setIsDetailOpen(false);
              openEditModal(apt);
            }} data-id="element-627" data-mp="uewq10p6zktw">
                  
                        Editar
                      </Button>}
                  <Button variant="secondary" onClick={() => setIsDetailOpen(false)} data-id="element-629" data-mp="bnvhpm26mcxo">
                  
                    Cerrar
                  </Button>
                </div>
              </div>;
      })()}
      </Modal>

      <Modal isOpen={isActionSelectOpen} onClose={() => setIsActionSelectOpen(false)} title="Gestionar Cita" size="sm" icon={<CalendarIcon className="w-6 h-6" data-id="element-631" data-mp="3t84aqjs4lvjg" />} accentColor="sky" data-id="element-630" data-mp="3o9jib6j48g5k">
        
        <div className="space-y-4" data-id="element-632" data-mp="2vlx5d7nzzz61">
          {selectedAppointment && (() => {
          const pt = getPatient(selectedAppointment.patientId);
          const doc = getDoctor(selectedAppointment.doctorId);
          const sIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
          const svcNames = services.filter(s => sIds.includes(s.id)).map(s => s.name).join(', ');
          return <div className="flex items-center gap-3 p-3.5 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-633" data-mp="2a9ybxfjix5mi">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center flex-shrink-0" data-id="element-634" data-mp="25bem341t79ov">
                    <UserIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-635" data-mp="1vcpeq57jh7mv" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-636" data-mp="1rau244k5r7ts">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-637" data-mp="1rcnb3qi8u44y">
                      {pt?.name || '-'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-638" data-mp="1hkrd0fg5qe0q">
                      {doc ? `${getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name)} ${doc.name}` : '-'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5" data-id="element-639" data-mp="17oxoryt2sw34">
                      {selectedAppointment.date} ·{' '}
                      {formatTime(selectedAppointment.time)}
                      {svcNames ? ` · ${svcNames}` : ''}
                    </p>
                  </div>
                </div>;
        })()}
          <p className="text-sm text-center text-gray-500 dark:text-gray-400 font-medium" data-id="element-640" data-mp="k0rgz0rnrlil">
            ¿Qué deseas hacer con esta cita?
          </p>
          <div className="space-y-2.5" data-id="element-641" data-mp="p0nj0ugsp2vi">
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openCompleteModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-green-400 dark:hover:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all group text-left" data-id="element-642" data-mp="1co55cahaj8r3">
              
              <div className="w-11 h-11 rounded-xl bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors" data-id="element-643" data-mp="20e7sqda9fqjd">
                <CheckIcon className="w-5 h-5 text-green-700 dark:text-green-400" data-id="element-644" data-mp="25rfx852unc7d" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-645" data-mp="2jib8fo0tm5e4">
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-646" data-mp="2ji2zu8bqdm8q">
                  Completar cita
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-647" data-mp="1qtxdjh513p6s">
                  Marcar como completada y programar próxima cita
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-green-500 transition-colors flex-shrink-0" data-id="element-648" data-mp="1muoj0a25zna6" />
            </button>
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openRescheduleModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-teal-400 dark:hover:border-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all group text-left" data-id="element-649" data-mp="1vsqnx0p0r02q">
              
              <div className="w-11 h-11 rounded-xl bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors" data-id="element-650" data-mp="1qog71ggurnxd">
                <RefreshCwIcon className="w-5 h-5 text-teal-700 dark:text-teal-400" data-id="element-651" data-mp="1zqflc10hgdwm" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-652" data-mp="24o8riu1sd7ww">
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-653" data-mp="24mxq30vtcc8y">
                  Reprogramar cita
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-654" data-mp="2efbu60ad5uya">
                  Cambiar la fecha y hora de la cita
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-teal-500 transition-colors flex-shrink-0" data-id="element-655" data-mp="2ieknj41hrqtb" />
            </button>
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openCancelModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-red-400 dark:hover:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all group text-left" data-id="element-656" data-mp="bt7u76wwuhlo">
              
              <div className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors" data-id="element-657" data-mp="qgswiylyocsj">
                <XIcon className="w-5 h-5 text-red-700 dark:text-red-400" data-id="element-658" data-mp="gr9chpmiicw3" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-659" data-mp="1e6n8ldyghxbq">
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-660" data-mp="1e7f5qt7pc344">
                  Cancelar cita
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-661" data-mp="19du5euimyx1r">
                  Cancelar y notificar al paciente y al doctor
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-662" data-mp="1xagz33bsq91u" />
            </button>
          </div>
          <button type="button" onClick={() => setIsActionSelectOpen(false)} className="w-full py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all" data-id="element-663" data-mp="1nknoa4jh9xyn">
            
            Volver
          </button>
        </div>
      </Modal>

      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nueva Cita" size="lg" icon={<CalendarIcon className="w-6 h-6" data-id="element-665" data-mp="2z0m2snxfdc79" />} accentColor="sky" data-id="element-664" data-mp="14n39xrecahsa">
        
        <form onSubmit={handleSubmit} data-id="element-666" data-mp="2yyi0gaf6cl93">{renderAppointmentForm(false)}</form>
      </Modal>
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="Editar Cita" size="lg" icon={<EditIcon className="w-6 h-6" data-id="element-668" data-mp="x2scmt3py243" />} accentColor="blue" data-id="element-667" data-mp="ndikjd8vkjfz">
        
        <form onSubmit={handleEditSubmit} data-id="element-669" data-mp="x4wqa2rf6fhk">{renderAppointmentForm(true)}</form>
      </Modal>

      <Modal isOpen={isCancelOpen} onClose={() => setIsCancelOpen(false)} title="Cancelar Cita" size="md" data-id="element-670" data-mp="sa8ynv14d4yr">
        
        <div className="space-y-4" data-id="element-671" data-mp="122y8ymkxocsi">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-672" data-mp="121vigfriwcd4">
            ¿Está seguro de que desea cancelar esta cita? Se notificará al
            paciente y al doctor.
          </p>
          <Textarea label="Motivo de cancelación" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Ingrese el motivo de la cancelación..." rows={3} data-id="element-673" data-mp="1b44r1038dvnv" />
          

          <div className="flex justify-end gap-3 modal-actions" data-id="element-674" data-mp="2rys59eqcu44v">
            <Button variant="outline" onClick={() => setIsCancelOpen(false)} data-id="element-675" data-mp="2rya3k2kxaxpk">
              Volver
            </Button>
            <Button variant="danger" onClick={handleCancel} data-id="element-676" data-mp="30qu3r7gv5aci">
              Cancelar Cita
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isRescheduleOpen} onClose={() => setIsRescheduleOpen(false)} title="Reprogramar Cita" size="md" data-id="element-677" data-mp="35olvo1hicwgp">
        
        <div className="space-y-4" data-id="element-678" data-mp="2d0zqiosmtjrq">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-679" data-mp="2d2134xpaqebk">
            Seleccione la nueva fecha y hora para la cita.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-680" data-mp="2i2ffut1uecfx">
            <Input type="date" label="Nueva Fecha" value={rescheduleData.date} onChange={e => setRescheduleData({
            ...rescheduleData,
            date: e.target.value
          })} data-id="element-681" data-mp="2n08jpffoaud1" />
            

            <Input type="time" label="Nueva Hora" value={rescheduleData.time} onChange={e => setRescheduleData({
            ...rescheduleData,
            time: e.target.value
          })} data-id="element-682" data-mp="1mqplageay5nc" />
            
          </div>
          <button type="button" onClick={() => setOverrideRestrictions(v => !v)} className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${overrideRestrictions ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600/60' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-683" data-mp="180a031lvyxp6">
            
            <span className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-md border-2 flex-shrink-0 transition-colors ${overrideRestrictions ? 'bg-amber-500 border-amber-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-684" data-mp="2oguolzw0r6s8">
              
              {overrideRestrictions && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-685" data-mp="369m4e6tz7gsr" />}
            </span>
            <span className="min-w-0" data-id="element-686" data-mp="3ky06gfjkj189">
              <span className="block text-sm font-semibold text-gray-900 dark:text-white" data-id="element-687" data-mp="3kwytg5izs4nw">
                Reprogramar sin restricciones
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-688" data-mp="2s89pzzugrn8z">
                Permite solapar con otra cita o ignorar el margen entre citas.
              </span>
            </span>
          </button>
          <div className="flex justify-end gap-3 modal-actions" data-id="element-689" data-mp="3280k40f4408j">
            <Button variant="outline" onClick={() => setIsRescheduleOpen(false)} data-id="element-690" data-mp="2x7mojhup6ire">
              
              Cancelar
            </Button>
            <Button onClick={handleReschedule} data-id="element-691" data-mp="hiupxbvg1gxk">Reprogramar</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isCompleteOpen} onClose={() => setIsCompleteOpen(false)} title="Completar Cita" size="lg" data-id="element-692" data-mp="k1fy5qm0bieo">
        
        <div className="space-y-4" data-id="element-693" data-mp="p1c3bt6fxfvt">
          {selectedAppointment && <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-694" data-mp="b19sus0gpoql">
              <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-695" data-mp="f2d6r8yy9ap6">
                <div data-id="element-696" data-mp="f2mx4xnwpjve">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-697" data-mp="f46b2hm3biu0">
                    Paciente:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-698" data-mp="17rk2y1axlo79">
                    {getPatient(selectedAppointment.patientId)?.name}
                  </p>
                </div>
                <div data-id="element-699" data-mp="y0z5wiaworqg">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-700" data-mp="y0fmsy9irxau">
                    Doctor:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-701" data-mp="1lp8nf8136tbs">
                    {(() => {
                  const doc = getDoctor(selectedAppointment.doctorId);
                  return doc ? `${getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name)} ${doc.name}` : '-';
                })()}
                  </p>
                </div>
                <div data-id="element-702" data-mp="1rb98wf5y365a">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-703" data-mp="1ren9bhv1t5a3">
                    Fecha:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-704" data-mp="1heeb5yhhx9s2">
                    {selectedAppointment.date} -{' '}
                    {formatTime(selectedAppointment.time)}
                  </p>
                </div>
                <div data-id="element-705" data-mp="18ndvwxi5pwd8">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-706" data-mp="18mkpxyr0vaw5">
                    Servicios:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-707" data-mp="yo58yolpli0d">
                    {(() => {
                  const sIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
                  return services.filter(s => sIds.includes(s.id)).map(s => s.name).join(', ') || '-';
                })()}
                  </p>
                </div>
                {selectedAppointment.createdByUserName && <div className="col-span-2" data-id="element-708" data-mp="vf46kfpyx17">
                    <span className="text-gray-500 dark:text-gray-400" data-id="element-709" data-mp="sj5gtw1s4bp">
                      Creada por:
                    </span>
                    <p className="font-medium text-gray-900 dark:text-white flex items-center gap-1.5" data-id="element-710" data-mp="as9yss8vgda7">
                      <UserIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-711" data-mp="fk1t9dv5f35m" />
                      {selectedAppointment.createdByUserName}
                    </p>
                  </div>}
              </div>
            </div>}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4" data-id="element-712" data-mp="3j720crj2gi49">
            <p className="text-sm text-green-800 dark:text-green-300" data-id="element-713" data-mp="1oq20h9hevdnw">
              ¿Confirmar que esta cita fue completada exitosamente?
            </p>
          </div>
          {/* Certificados de regalo canjeables: coinciden con la PERSONA y el
                    SERVICIO de esta cita, están activos y vigentes. */}
          {selectedAppointment && (() => {
          const sIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId].filter(Boolean);
          const alreadyRedeemed = !!selectedAppointment.giftCertificateId;
          const allCerts = getActiveGiftCertificates(selectedAppointment.patientId);
          // Devuelve el servicio (item) pendiente del certificado que
          // corresponde a esta cita, para mostrar su nombre y precio.
          const matchingItem = (c: GiftCertificate) => {
            const items = c.items && c.items.length > 0 ? c.items : undefined;
            if (items) {
              return items.find(it => !it.redeemed && sIds.includes(it.serviceId));
            }
            if (sIds.includes(c.serviceId)) {
              return {
                serviceName: c.serviceName,
                frozenPrice: c.frozenPrice,
                offerDiscountPercent: c.offerDiscountPercent
              };
            }
            return undefined;
          };
          const redeemable = allCerts.filter(c => !!matchingItem(c));
          if (alreadyRedeemed) {
            return <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 flex items-center gap-2 text-sm text-purple-700 dark:text-purple-300" data-id="element-714" data-mp="2628x1m5utphw">
                    <GiftIcon className="w-4 h-4 flex-shrink-0" data-id="element-715" data-mp="49u9o7kjjsu3" />
                    Esta cita ya está marcada como pagada con un certificado de
                    regalo.
                  </div>;
          }
          if (redeemable.length === 0) return null;
          return <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4" data-id="element-716" data-mp="3p16kfh9mzcvm">
                  <div className="flex items-center gap-2 mb-2" data-id="element-717" data-mp="3l0waxummy4c0">
                    <GiftIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" data-id="element-718" data-mp="lidadeg2h667" />
                    <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300" data-id="element-719" data-mp="glmvgug00gmd">
                      Certificado de regalo disponible
                    </h4>
                  </div>
                  <p className="text-xs text-purple-700/80 dark:text-purple-300/70 mb-3" data-id="element-720" data-mp="7r8iiyixxi6f">
                    Este paciente tiene un certificado activo para un servicio
                    de esta cita. Puedes canjearlo ahora y quedará vinculado.
                  </p>
                  <div className="space-y-2" data-id="element-721" data-mp="2i6h6f6yvis42">
                    {redeemable.map(c => {
                const item = matchingItem(c);
                const hasMultiple = c.items && c.items.length > 1 ? true : false;
                return <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors border ${certToRedeem === c.id ? 'bg-white dark:bg-gray-800 border-purple-300 dark:border-purple-600' : 'border-transparent hover:bg-white/60 dark:hover:bg-gray-800/60'}`} data-id="element-722" data-mp="2r98i97vnvpw8">
                        
                          <input type="radio" name="cert-redeem" checked={certToRedeem === c.id} onChange={() => setCertToRedeem(certToRedeem === c.id ? '' : c.id)} className="w-4 h-4 text-purple-600 focus:ring-purple-500" data-id="element-723" data-mp="3avoeoxo4qw2i" />
                        

                          <div className="flex-1 min-w-0" data-id="element-724" data-mp="2use6tnh4zt32">
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-725" data-mp="2urw0fy0rfyhf">
                              <span className="font-mono" data-id="element-726" data-mp="38rnbpqzw70rq">{c.code}</span> ·{' '}
                              {item?.serviceName || c.serviceName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-727" data-mp="2h22epokjxubx">
                              {formatCurrency(item?.frozenPrice ?? c.frozenPrice)}
                              {item?.offerDiscountPercent ? ` (oferta -${item.offerDiscountPercent}% congelada)` : ''}{' '}
                              · vence {c.expiresAt}
                              {hasMultiple ? ' · certificado con varios servicios' : ''}
                            </p>
                          </div>
                        </label>;
              })}
                    {certToRedeem && <button type="button" onClick={() => setCertToRedeem('')} className="text-xs text-gray-500 dark:text-gray-400 hover:underline" data-id="element-728" data-mp="1o0163d16sui8">
                    
                        No canjear ahora
                      </button>}
                  </div>
                </div>;
        })()}
          {/* Tarjetas de fidelidad: 7 visitas completadas y una de las tres
                    opciones de la tarjeta debe estar incluida en esta cita. */}
          {selectedAppointment && (() => {
          const appointmentServices = selectedAppointment.serviceIds?.length ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
          const redeemableCards = loyaltyCards.filter(card => card.service_ids.some(id => appointmentServices.includes(id)));
          if (redeemableCards.length === 0) return null;
          return <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4" data-id="element-729" data-mp="2uf1bodk58jsk">
              <div className="flex items-center gap-2 mb-2" data-id="element-730" data-mp="2goeb0futojtu"><GiftIcon className="w-4 h-4 text-amber-600" data-id="element-731" data-mp="2gl9zsyixwnto" /><h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200" data-id="element-732" data-mp="2kpq2ys165h57">Tarjeta de fidelidad disponible</h4></div>
              <p className="text-xs text-amber-700 dark:text-amber-300 mb-3" data-id="element-733" data-mp="38f29fk543b3t">Este paciente completó sus 7 visitas. Puedes canjear una tarjeta sólo por el servicio coincidente de esta cita.</p>
              <div className="space-y-2" data-id="element-734" data-mp="2zd2quneg7yyr">{redeemableCards.map(card => <label key={card.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border ${loyaltyCardToRedeem === card.id ? 'bg-white dark:bg-gray-800 border-amber-400' : 'border-transparent hover:bg-white/60'}`} data-id="element-735" data-mp="34c5xgca2ud8u"><input type="radio" name="loyalty-redeem" checked={loyaltyCardToRedeem === card.id} onChange={() => setLoyaltyCardToRedeem(loyaltyCardToRedeem === card.id ? '' : card.id)} className="w-4 h-4 text-amber-600" data-id="element-736" data-mp="1syjxdw2oir8j" /><div data-id="element-737" data-mp="1ge4wu9g5vdc2"><p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-738" data-mp="1ged9c08dggmq">{card.code}</p><p className="text-xs text-gray-500" data-id="element-739" data-mp="1udmpizwkvaqp">Servicios elegidos: {card.service_ids.map(id => services.find(s => s.id === id)?.name || 'Servicio').join(' · ')}</p></div></label>)}</div>
              {loyaltyCardToRedeem && <button type="button" onClick={() => setLoyaltyCardToRedeem('')} className="text-xs text-gray-500 mt-2 hover:underline" data-id="element-740" data-mp="11qs4o1vum9cp">No canjear ahora</button>}
            </div>;
        })()}
          {loyaltyCardToRedeem ? <div className="border border-amber-200 dark:border-amber-800 rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20" data-id="element-741" data-mp="1bhwn5bkqgcca">
              <p className="text-sm text-amber-800 dark:text-amber-200" data-id="element-742" data-mp="iz95y78uxhgh">
                Esta cita se completa con tarjeta de fidelidad, así que no lleva
                factura.
              </p>
            </div> : <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800" data-id="element-743" data-mp="2e377nol5bhjb">
              <Input label="Número de Factura *" placeholder="Ej: FAC-001234" value={completeData.invoiceNumber} onChange={e => setCompleteData({
            ...completeData,
            invoiceNumber: e.target.value
          })} required data-id="element-744" data-mp="2j0ql4ma0rqa7" />
            </div>}
          <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-745" data-mp="1v54mxlzms6rb">
            <div className="flex items-center gap-2 mb-4" data-id="element-746" data-mp="203pekx0thi45">
              <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-747" data-mp="244rdfcqtns5l" />
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-748" data-mp="246m12yth9ydd">
                Próxima Cita Sugerida
              </h4>
              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full" data-id="element-749" data-mp="3fk69meb7ifzn">
                Opcional
              </span>
            </div>
            <div className="space-y-4" data-id="element-750" data-mp="3umeqqsb80vkb">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-751" data-mp="3uld6bx8bewpx">
                <Input type="date" label="Fecha Sugerida" value={completeData.nextAppointmentDate} onChange={e => setCompleteData({
                ...completeData,
                nextAppointmentDate: e.target.value
              })} min={getToday()} data-id="element-752" data-mp="3pn3b5mmlbw4b" />
                

                <Input type="time" label="Hora Sugerida" value={completeData.nextAppointmentTime} onChange={e => setCompleteData({
                ...completeData,
                nextAppointmentTime: e.target.value
              })} data-id="element-753" data-mp="36w2amu5tnsin" />
                
              </div>
              <div data-id="element-754" data-mp="w4sm4hwbnbye">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-755" data-mp="w5kab7nla7en">
                  Servicios para la Próxima Cita
                </label>
                {completeData.nextServiceIds.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-756" data-mp="184zdbpbpt91k">
                    {completeData.nextServiceIds.map(sId => {
                  const svc = getService(sId);
                  if (!svc) return null;
                  return <span key={sId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200" data-id="element-757" data-mp="kgpwz2x9tdht">
                        
                          {svc.name}
                          <button type="button" onClick={() => setCompleteData(prev => ({
                      ...prev,
                      nextServiceIds: prev.nextServiceIds.filter(id => id !== sId)
                    }))} className="ml-1 hover:text-teal-900" data-id="element-758" data-mp="fjpoklcy7ynj">
                          
                            <XIcon className="w-3 h-3" data-id="element-759" data-mp="3s31wmi3gdwy1" />
                          </button>
                        </span>;
                })}
                  </div>}
                <div className="relative mb-2" data-id="element-760" data-mp="5uaj6ch16lbb">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-761" data-mp="5szf0bjobbjo" />
                  <input type="text" placeholder="Buscar servicios para agregar..." value={serviceSearchTerm} onChange={e => setServiceSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-762" data-mp="fklvones62tt" />
                  
                </div>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-763" data-mp="3iebbi86uhc7l">
                  {availableServices.filter(s => serviceSearchTerm ? s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) : true).map(service => {
                  const isSelected = completeData.nextServiceIds.includes(service.id);
                  return <label key={service.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-white dark:hover:bg-gray-800'}`} data-id="element-764" data-mp="dyiie3hlbm9q">
                        
                          <input type="checkbox" checked={isSelected} onChange={() => setCompleteData(prev => ({
                      ...prev,
                      nextServiceIds: isSelected ? prev.nextServiceIds.filter(id => id !== service.id) : [...prev.nextServiceIds, service.id]
                    }))} className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500" data-id="element-765" data-mp="23g71wdmsjn91" />
                        

                          <div className="flex-1 min-w-0" data-id="element-766" data-mp="16r7raryqi9do">
                            <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-767" data-mp="11r3bl45nhioi">
                              {service.name}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2" data-id="element-768" data-mp="31vfk607t0mns">
                              {service.duration} min
                            </span>
                          </div>
                        </label>;
                })}
                </div>
              </div>
              <Textarea label="Notas para la Próxima Cita" value={completeData.nextAppointmentNotes} onChange={e => setCompleteData({
              ...completeData,
              nextAppointmentNotes: e.target.value
            })} placeholder="Ej: Control post-tratamiento, siguiente sesión..." rows={2} data-id="element-769" data-mp="2wwcfigvqkigc" />
              
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 modal-actions" data-id="element-770" data-mp="7bpx3cgivfi5">
            <Button variant="outline" onClick={() => setIsCompleteOpen(false)} data-id="element-771" data-mp="c4aoy8fvbij1">
              Cancelar
            </Button>
            <Button onClick={handleComplete} leftIcon={<CheckIcon className="w-4 h-4" data-id="element-773" data-mp="1rw4ou0av2sqg" />} data-id="element-772" data-mp="1wsv78ua520f1">
              
              Completar Cita
            </Button>
          </div>
        </div>
      </Modal>

      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Citas" isExporting={isExporting} statusOptions={statusOptions} patientOptions={patientOptions} data-id="element-774" data-mp="25okmelkztyjz" />
      

      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Citas a PDF" isExporting={isPdfExporting} statusOptions={statusOptions} patientOptions={patientOptions} data-id="element-775" data-mp="18yttgqscojga" />
      
    </div>;
}